import React, { useEffect, useState, useRef, useMemo } from 'react';
import { View, Text, Pressable } from '../lib/react-native-compat';
import { useAtom } from 'jotai';
import { loadTeamCostDetail, loadTeamCostDetailProjections, pNum, fNum, loadEquipmentData, loadSummaryWIPArray, loadJDEValues, loadBudgetExistsArray, loadHAExistsArray, loadUserData, loadTeamUserDataMain } from '../db/company';
import { dataSourceVersionAtom, isLiveVersionAtom, selectedVersionAtom, selectedTeamAtom, dataSourceJobsAtom, overheadDataAtom, selectedWIPAtom, projectCompleteAtom, ohPercentAtom } from '../store/company';
import { jdeEnvAtom, buildOrchUrl, getOrchAuth, getReadEnv, envColor, JDE_ENV_PROD, JDE_ENV_TEST, JDE_ENV_DEV } from '../helpers/envResolver';
import { aggregateEACRows, haZeroLines, remapMissingBasePhase } from '../helpers/eacAggregation';
import { EAC_FILE_NAME, inputFolderPath, folderWriteSupported, getSavedFolder, pickFolder, ensureWriteAccess, existingFileSize, writeTextFile } from '../helpers/jdeInputFolder';
import { aggregateBudgetRows, uploadBudgetPayload, processBudgetPayload } from '../helpers/jdeBudgetUpload';
import moment from 'moment';
import xlsx from 'xlsx';
import Summary from './summary';

// Display names for Business_Unit (Cat_Code_BU_27) codes in the team filter.
// SWT is the West Texas region (topMenu files it under WTX).
const TEAM_LABELS = {
    SWT: 'West Texas',
    NOR: 'Northern',
    EASTERN: 'Eastern',
    MCLEANS: 'Mcleans',
    'GULF COAST': 'Gulf Coast',
    PICKETT: 'Pickett',
    ED: 'Energy Delivery',
};

const ProjectionSync = () => {
    const [activeTab, setActiveTab] = useState('Projections');
    const [jdeEnv, setJdeEnv] = useAtom(jdeEnvAtom);

    // ---- All state copied from runProjections ----
    const [versions] = useAtom(dataSourceVersionAtom)
    const [versionInfo, setVersionInfo] = useState({})
    const [isLiveVersion] = useAtom(isLiveVersionAtom);
    const [selectedVersion] = useAtom(selectedVersionAtom);
    const [selectedTeam] = useAtom(selectedTeamAtom);
    const [jobs] = useAtom(dataSourceJobsAtom);
    const [selectedWIP] = useAtom(selectedWIPAtom);
    const [ohPercent, setOhPercent] = useAtom(ohPercentAtom)
    const [overheadData] = useAtom(overheadDataAtom)
    const [filteredJobs, setFilteredJobs] = useState([])
    const [curBudget, setCurBudget] = useState({})
    const [fetchedAllJobUserData, setFetchedAllJobUserData] = useState(false)
    const [completionDates, setCompletionDates] = useState({})
    const [physicalComplete, setPhysicalComplete] = useState({})
    const [actualCost, setActualCost] = useState({})
    const [actualOverhead, setActualOverhead] = useState({})
    const [openCommitments, setOpenCommitments] = useState({})
    const [ETC, setETC] = useState({})
    const [EAC, setEAC] = useState({})
    const [jobProfit, setJobProfit] = useState({})
    const [jobProfitPerc, setJobProfitPerc] = useState({})
    const [equipmentUserData, setEquipmentUserData] = useState({})
    const [labor, setLabor] = useState({})
    const [laborCostType, setLaborCostType] = useState({})
    const [equipmentCostType, setEquipmentCostType] = useState({})
    const [equipmentPhased, setEquipmentPhased] = useState({})
    const [materials, setMaterials] = useState({})
    const [rentals, setRentals] = useState({})
    const [subcontractors, setSubcontractors] = useState({})
    const [other, setOther] = useState({})
    const [contingency, setContingency] = useState({})
    const [incentive, setIncentive] = useState({})
    const [total, setTotal] = useState({})
    const [grossMargin, setGrossMargin] = useState({})
    const [grossMarginPerc, setGrossMarginPerc] = useState({})
    const [backlog, setBacklog] = useState({})
    const [costPercent, setCostPercent] = useState({})
    const [actualHours, setActualHours] = useState({})
    const [hoursETC, setHoursETC] = useState({})
    const [earnedRevenue, setEarnedRevenue] = useState({})
    const [billings, setBillings] = useState({})
    const [overUnderBilled, setOverUnderBilled] = useState({})
    const [remainingToBill, setRemainingToBill] = useState({})
    const [earnedRevenueRows, setEarnedRevenueRows] = useState({})
    const [etcOverhead, setETCOverhead] = useState({})
    const [totalBudget, setTotalBudget] = useState(0)

    const [etcPieces, setEtcPieces] = useState({})
    const [laborPieces, setLaborPieces] = useState({})
    const [laborPhasedPieces, setLaborPhasedPieces] = useState({})
    const [equipmentPieces, setEquipmentPieces] = useState({})
    const [equipmentPhasedPieces, setEquipmentPhasedPieces] = useState({})
    const [openCommitmentsPieces, setOpenCommitmentsPieces] = useState({})
    const [projectionCost, setProjectionCost] = useState([])
    const [contingencyPieces, setContingencyPieces] = useState({})
    const [projectionRows, setProjectionRows] = useState([])
    const [equipRates, setEquipRates] = useState({})
    const [adminButton, setAdminButton] = useState(true)
    const [jdeValues, setJDEValues] = useState([])
    const [summaryETCValues, setSummaryETCValues] = useState({})
    const [summaryData, setSummaryData] = useState(null)
    const [rawUserData, setRawUserData] = useState(null)
    const [rawCostDetailData, setRawCostDetailData] = useState(null)
    const [rawCommitmentsData, setRawCommitmentsData] = useState(null)
    const [rawBillingsData, setRawBillingsData] = useState(null)
    const [jobComparison, setJobComparison] = useState({})
    const [sortedFilteredJobs, setSortedFilteredJobs] = useState([])
    const [jdeBudgetExists, setJdeBudgetExists] = useState({})
    const [budgetCheckLoading, setBudgetCheckLoading] = useState(false)
    const [jdeHAExists, setJdeHAExists] = useState({})
    const [allJobs, setAllJobs] = useState([])
    const [dayZeroData, setDayZeroData] = useState({})
    const [budgetSyncing, setBudgetSyncing] = useState(false)
    const [budgetSyncProgress, setBudgetSyncProgress] = useState({ current: 0, total: 0 })
    const [budgetSyncResults, setBudgetSyncResults] = useState(null)
    const [showBudgetSyncModal, setShowBudgetSyncModal] = useState(false)
    const [budgetJobsToSync, setBudgetJobsToSync] = useState([])
    const [budgetJobsBlockedByHA, setBudgetJobsBlockedByHA] = useState([])
    const [selectedBudgetJobs, setSelectedBudgetJobs] = useState([])
    const [showProcessBudgetModal, setShowProcessBudgetModal] = useState(false)
    const [processBudgetRunning, setProcessBudgetRunning] = useState(false)
    const [processBudgetResult, setProcessBudgetResult] = useState(null)
    const [haDataLoaded, setHaDataLoaded] = useState(false)
    const [projectionDataLoaded, setProjectionDataLoaded] = useState(false)
    const [syncPeriod, setSyncPeriod] = useState(null)
    const [eacSyncing, setEacSyncing] = useState(false)
    const [eacSyncResult, setEacSyncResult] = useState(null)
    const [showSyncModal, setShowSyncModal] = useState(false)
    // Sync EAC writes FusionEAC.csv straight into the JDE input folder (see jdeInputFolder.js)
    const [eacFolder, setEacFolder] = useState(null)
    const [eacFolderError, setEacFolderError] = useState('')
    const [eacZeroLines, setEacZeroLines] = useState(null) // null while loading
    const [eacZeroInfo, setEacZeroInfo] = useState({})
    const [eacRows, setEacRows] = useState(null) // file lines after moving no-phase lines off missing 100105 accounts
    const [eacWriting, setEacWriting] = useState(false)
    const [eacCsvPreparing, setEacCsvPreparing] = useState(false)
    const [coRows, setCoRows] = useState([])
    const [coRawRecords, setCoRawRecords] = useState([])
    const [coLoading, setCoLoading] = useState(false)
    const [coSyncing, setCoSyncing] = useState(false)
    const [coSyncProgress, setCoSyncProgress] = useState({ current: 0, total: 0 })
    const [coSyncResults, setCoSyncResults] = useState(null)
    const [showCoSyncModal, setShowCoSyncModal] = useState(false)
    const [coStatusFilter, setCoStatusFilter] = useState('All')
    const [teamFilter, setTeamFilter] = useState('All')

    // ---- Version info ----
    useEffect(() => {
        versions && versions.length > 0 && setVersionInfo(versions.find(v => v.Version_GUID === selectedVersion))
    }, [versions, selectedVersion]);

    // ---- Filter jobs by team ----
    useEffect(() => {
        let filtered = []
        filtered = jobs ? jobs.filter(a => a.Cat_Code_BU_27 == selectedTeam).filter(a => a.Cat_Code_BU_08 != 'T&M') : []
        if (adminButton) {
            filtered = jobs ? jobs.filter(a => a.Cat_Code_BU_08 != 'T&M') : []
            setFetchedAllJobUserData(false)
        }
        setFilteredJobs(filtered)
        setHaDataLoaded(false)
        setProjectionDataLoaded(false)
    }, [jobs, adminButton])

    // ---- All jobs (all teams, non-T&M) for Budget & Projections tabs ----
    useEffect(() => {
        const all = jobs ? jobs.filter(a => a.Cat_Code_BU_08 != 'T&M') : []
        setAllJobs(all)
    }, [jobs])

    // ---- Team filter (Projections tab) ----
    // Narrows the table, the Excel export, the CSV download and the sync counts.
    // Data still loads for every job so switching teams doesn't refetch.
    const teamOptions = useMemo(() => {
        const counts = {}
        filteredJobs.forEach(j => {
            const code = j.Cat_Code_BU_27 || ''
            counts[code] = (counts[code] || 0) + 1
        })
        return Object.keys(counts)
            .filter(code => code)
            .sort((a, b) => (TEAM_LABELS[a] || a).localeCompare(TEAM_LABELS[b] || b))
            .map(code => ({ code, count: counts[code], label: TEAM_LABELS[code] ? `${TEAM_LABELS[code]} (${code})` : code }))
    }, [filteredJobs])

    const visibleJobs = useMemo(() => (
        teamFilter === 'All' ? filteredJobs : filteredJobs.filter(j => j.Cat_Code_BU_27 === teamFilter)
    ), [filteredJobs, teamFilter])

    const visibleProjectionRows = useMemo(() => {
        if (teamFilter === 'All') return projectionRows
        const ids = new Set(visibleJobs.map(j => j.BU_ID))
        return projectionRows.filter(r => ids.has(r.jobId))
    }, [projectionRows, visibleJobs, teamFilter])

    // ---- Job checkboxes: when any visible job is ticked, the exports and the
    // JDE sync cover only the ticked jobs; otherwise every visible job ----
    const [syncSelection, setSyncSelection] = useState(() => new Set())
    const exportJobs = useMemo(() => {
        const ticked = visibleJobs.filter(j => syncSelection.has(j.BU_ID))
        return ticked.length ? ticked : visibleJobs
    }, [visibleJobs, syncSelection])
    const exportProjectionRows = useMemo(() => {
        if (exportJobs === visibleJobs) return visibleProjectionRows
        const ids = new Set(exportJobs.map(j => j.BU_ID))
        return visibleProjectionRows.filter(r => ids.has(r.jobId))
    }, [exportJobs, visibleJobs, visibleProjectionRows])
    const tickedCount = exportJobs === visibleJobs ? 0 : exportJobs.length
    const allVisibleTicked = visibleJobs.length > 0 && visibleJobs.every(j => syncSelection.has(j.BU_ID))

    const toggleSyncJob = (id) => setSyncSelection(prev => {
        const next = new Set(prev)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        return next
    })
    // Tick the shown jobs whose Fusion projection doesn't match JDE HA
    const selectOutOfSyncJobs = () => setSyncSelection(new Set(
        visibleJobs.filter(j => {
            const comp = jobComparison[j.BU_ID]
            return comp && !comp.inSync && comp.source === 'HA'
        }).map(j => j.BU_ID)
    ))
    const toggleAllVisibleJobs = () => setSyncSelection(prev => {
        const next = new Set(prev)
        visibleJobs.forEach(j => (allVisibleTicked ? next.delete(j.BU_ID) : next.add(j.BU_ID)))
        return next
    })

    const teamFileSuffix = (teamFilter === 'All' ? '' : '-' + teamFilter.replace(/\s+/g, ''))
        + (tickedCount ? `-${tickedCount}jobs` : '')

    const eacFileLineCount = useMemo(() => (
        showSyncModal ? aggregateEACRows(exportProjectionRows).length : 0
    ), [showSyncModal, exportProjectionRows])

    // ---- JDE input folder remembered for the selected environment ----
    useEffect(() => {
        let cancelled = false
        setEacFolder(null)
        setEacFolderError('')
        getSavedFolder(jdeEnv).then(h => { if (!cancelled) setEacFolder(h) })
        return () => { cancelled = true }
    }, [jdeEnv])

    // ---- Load GL account data + JDE values ----
    // Compute WIP date: first day of the month AFTER the selected period (so the query captures the full period)
    const haWipDate = (() => {
        const p = syncPeriod || (() => {
            const today = new Date();
            const d = today.getDate();
            const m = d <= 15 ? today.getMonth() : today.getMonth() + 1;
            const y = today.getFullYear();
            return { periodNbr: (m === 0 ? 12 : m).toString().padStart(2, '0'), key: `${m === 0 ? y - 1 : m > 12 ? y + 1 : y}-${(m === 0 ? 12 : m > 12 ? 1 : m).toString().padStart(2, '0')}` };
        })();
        const [yr, mo] = (p.key || '').split('-').map(Number);
        if (!yr || !mo) return new Date().toISOString().slice(0, 10);
        // First day of next month
        const nextMonth = new Date(yr, mo, 1);
        return nextMonth.toISOString().slice(0, 10);
    })();

    useEffect(() => {
        if (filteredJobs.length) {
            setHaDataLoaded(false)
            setJobComparison({})
            setSortedFilteredJobs([])
            const jobIds = filteredJobs.map(j => j.BU_ID)
            loadJDEValues({ ids: jobIds, wip: haWipDate, jdeEnv: getReadEnv(jdeEnv) }).then((data) => {
                setJDEValues(data?.data)
                setHaDataLoaded(true)
            }).catch(() => {
                setHaDataLoaded(true)
            })

        }
    }, [filteredJobs, haWipDate, jdeEnv])

    // ---- Batch check JA budget existence for ALL jobs ----
    useEffect(() => {
        if (allJobs.length) {
            setBudgetCheckLoading(true)
            setJdeBudgetExists({})
            const jobIds = allJobs.map(j => j.BU_ID)
            loadBudgetExistsArray({ ids: jobIds, jdeEnv: getReadEnv(jdeEnv) }).then((data) => {
                const results = {}
                // Mark jobs that have JA budget rows
                if (data?.data) {
                    data.data.forEach(row => {
                        results[row.BU_ID?.trim()] = true
                    })
                }
                // Mark jobs NOT in the result as false
                jobIds.forEach(id => {
                    if (results[id] === undefined) results[id] = false
                })
                setJdeBudgetExists(results)
                setBudgetCheckLoading(false)
            }).catch(() => {
                setBudgetCheckLoading(false)
            })
        }
    }, [allJobs, jdeEnv])

    // ---- Batch check HA (actuals) existence for ALL jobs — blocks budget upload ----
    useEffect(() => {
        if (allJobs.length) {
            setJdeHAExists({})
            const jobIds = allJobs.map(j => j.BU_ID)
            loadHAExistsArray({ ids: jobIds, jdeEnv: getReadEnv(jdeEnv) }).then((data) => {
                const results = {}
                if (data?.data) {
                    data.data.forEach(row => {
                        results[row.BU_ID?.trim()] = true
                    })
                }
                jobIds.forEach(id => {
                    if (results[id] === undefined) results[id] = false
                })
                setJdeHAExists(results)
            }).catch(() => { })
        }
    }, [allJobs, jdeEnv])

    // ---- Overhead ETC ----
    useEffect(() => {
        const jobIds = filteredJobs.map(j => j.BU_ID)
        const ohTeamPercent = {}
        filteredJobs.forEach(job => {
            ohTeamPercent[job.BU_ID] = overheadData[job.Cat_Code_BU_27] ? overheadData[job.Cat_Code_BU_27] : ''
        })
        const ohETC = {}
        jobIds.forEach((job) => {
            const ohPer = ohTeamPercent[job] ? pNum(ohTeamPercent[job]) : 0
            const oh = curBudget[job] ? earnedRevenueRows[job] ? (pNum(curBudget[job]) + pNum(earnedRevenueRows[job])) * ohPer / 100 : pNum(curBudget[job]) * ohPer / 100 : ''
            const ohFormatted = oh < 0 ? '' : isNaN(pNum(oh)) ? 0 : pNum(oh)
            ohETC[job] = ohFormatted
        })
        setETCOverhead(ohETC)
    }, [earnedRevenueRows, overheadData, curBudget])

    useEffect(() => {
        if (selectedTeam) {
            setOhPercent(overheadData[selectedTeam] ? overheadData[selectedTeam] : '')
        }
    }, [selectedTeam, overheadData])

    // ---- Over/Under Billed ----
    useEffect(() => {
        const jobIds = filteredJobs.map(j => j.BU_ID)
        const oub = {}
        const rtb = {}
        jobIds.forEach((job) => {
            oub[job] = pNum(billings[job]) - pNum(earnedRevenue[job])
            rtb[job] = pNum(curBudget[job]) - pNum(billings[job])
        })
        setOverUnderBilled(oub)
        setRemainingToBill(rtb)
    }, [billings, earnedRevenue, curBudget, filteredJobs])

    // ---- Main data loading (copied from runProjections) ----
    useEffect(() => {
        if (!fetchedAllJobUserData && filteredJobs?.length > 0) {
            setFetchedAllJobUserData(true)

            const jobIds = filteredJobs.map(j => j.BU_ID)

            loadEquipmentData({ forecastGuid: selectedVersion }).then((data) => {
                const rData = {}
                data?.data.forEach((d) => {
                    if (d?.Forecast_Tab === 'Equipment') {
                        if (rData[d?.Forecast_Row_ID]) {
                            rData[d?.Forecast_Row_ID][d.Forecast_Field_Name] = d.Forecast_Value
                        } else {
                            rData[d?.Forecast_Row_ID] = {
                                id: d?.Forecast_Row_ID,
                                [d.Forecast_Field_Name]: d.Forecast_Value
                            }
                        }
                    }
                })
                const rates = {}
                Object.values(rData).forEach((row) => {
                    rates[row['Equipment Tier']] = row['Current Hourly Billing Rate']
                })
                setEquipRates(rates)
            })

            loadTeamCostDetailProjections({ ids: jobIds, versions: [selectedVersion], wip: moment().add(1, 'month').format('YYYY-MM-DD') }).then((data) => {
                setProjectionCost(data?.data)
            })

            const wipDate = selectedWIP?.Version_Start_DateTime ? moment(selectedWIP?.Version_Start_DateTime).endOf('month').format('YYYY-MM-DD') : ''
            let revenuePrevPeriod = {}
            loadTeamCostDetail({ ids: jobIds, forecastGuid: selectedVersion, wip: wipDate }).then((data) => {
                data?.data?.forEach((d) => {
                    if (d?.Cost_Category == 'Revenue') {
                        revenuePrevPeriod[d?.Job_Number] = (isNaN(pNum(d?.Total_Job_Cost)) ? 0 : pNum(d?.Total_Job_Cost))
                    }
                })
                setEarnedRevenueRows(revenuePrevPeriod)
            })
        }
    }, [filteredJobs])

    // ---- Process raw cost detail data from Summary ----
    useEffect(() => {
        if (!rawCostDetailData) return
        let actualCostLocal = {}
        let actualOverheadLocal = {}
        let hoursLocal = {}
        rawCostDetailData.forEach((d) => {
            if ((d?.Cost_Category == 'Job Cost - All Others' || d?.Cost_Category == 'Job Cost - Labor') && actualCostLocal[d?.Job_Number]) {
                actualCostLocal[d?.Job_Number] = actualCostLocal[d?.Job_Number] + (isNaN(pNum(d?.Total_Job_Cost)) ? 0 : pNum(d?.Total_Job_Cost))
            } else if (d?.Cost_Category == 'Job Cost - All Others' || d?.Cost_Category == 'Job Cost - Labor') {
                actualCostLocal[d?.Job_Number] = (isNaN(pNum(d?.Total_Job_Cost)) ? 0 : pNum(d?.Total_Job_Cost))
            }
            if (d?.Cost_Category == 'OH' && actualOverheadLocal[d?.Job_Number]) {
                actualOverheadLocal[d?.Job_Number] = actualOverheadLocal[d?.Job_Number] + (isNaN(pNum(d?.Total_Job_Cost)) ? 0 : pNum(d?.Total_Job_Cost))
            } else if (d?.Cost_Category == 'OH') {
                actualOverheadLocal[d?.Job_Number] = (isNaN(pNum(d?.Total_Job_Cost)) ? 0 : pNum(d?.Total_Job_Cost))
            }
            if (d?.Cost_Category == 'Job Cost - Labor' && hoursLocal[d?.Job_Number]) {
                hoursLocal[d?.Job_Number] = hoursLocal[d?.Job_Number] + (isNaN(pNum(d?.Total_Hours)) ? 0 : pNum(d?.Total_Hours))
            } else if (d?.Cost_Category == 'Job Cost - Labor') {
                hoursLocal[d?.Job_Number] = (isNaN(pNum(d?.Total_Hours)) ? 0 : pNum(d?.Total_Hours))
            }
        })
        setActualHours(hoursLocal)
        setActualCost(actualCostLocal)
        setActualOverhead(actualOverheadLocal)
    }, [rawCostDetailData])

    // ---- Process raw commitments data from Summary ----
    useEffect(() => {
        if (!rawCommitmentsData) return
        let openCommitmentsLocal = {}
        let openCommitmentsPiecesLocal = {}
        rawCommitmentsData.forEach((d) => {
            if (d?.Cost_Type_Code?.[0] == '5' && openCommitmentsLocal[d?.Job_Number]) {
                openCommitmentsLocal[d?.Job_Number] = openCommitmentsLocal[d?.Job_Number] + (isNaN(pNum(d?.PO_True_Open_Amount)) ? 0 : pNum(d?.PO_True_Open_Amount))
            } else if (d?.Cost_Type_Code?.[0] == '5') {
                openCommitmentsLocal[d?.Job_Number] = (isNaN(pNum(d?.PO_True_Open_Amount)) ? 0 : pNum(d?.PO_True_Open_Amount))
            }
            // Pieces must carry only what the openCommitments total above counts (5xxxx)
            // so the exported rows sum to the displayed EAC exactly.
            if (d?.Cost_Type_Code?.[0] == '5') {
                const costCode = d.Cost_Code ? d.Cost_Code : '100105'
                if (!openCommitmentsPiecesLocal[d?.Job_Number]) {
                    openCommitmentsPiecesLocal[d?.Job_Number] = {}
                }
                if (!openCommitmentsPiecesLocal[d?.Job_Number][d?.Cost_Type_Code]) {
                    openCommitmentsPiecesLocal[d?.Job_Number][d?.Cost_Type_Code] = {}
                }
                if (!openCommitmentsPiecesLocal[d?.Job_Number][d?.Cost_Type_Code][costCode]) {
                    openCommitmentsPiecesLocal[d?.Job_Number][d?.Cost_Type_Code][costCode] = 0
                }
                openCommitmentsPiecesLocal[d?.Job_Number][d?.Cost_Type_Code][costCode] = openCommitmentsPiecesLocal[d?.Job_Number][d?.Cost_Type_Code][costCode] + (isNaN(pNum(d?.PO_True_Open_Amount)) ? 0 : pNum(d?.PO_True_Open_Amount))
            }
        })
        setOpenCommitments(openCommitmentsLocal)
        setOpenCommitmentsPieces(openCommitmentsPiecesLocal)
    }, [rawCommitmentsData])

    // ---- Process raw billings data from Summary ----
    useEffect(() => {
        if (!rawBillingsData) return
        const billingData = {}
        const billingsLocal = {}
        rawBillingsData.forEach((d) => {
            if (billingData[d?.Job_Number]) {
                billingData[d?.Job_Number].push(d)
            } else {
                billingData[d?.Job_Number] = [d]
            }
        })
        Object.values(billingData).forEach((bills) => {
            let invoices = 0
            bills.forEach((d) => {
                invoices = invoices + pNum(d?.Invoice_Amount)
            })
            billingsLocal[bills[0]?.Job_Number] = invoices
        })
        setBillings(billingsLocal)
    }, [rawBillingsData])

    // ---- Process raw user data from Summary ----
    useEffect(() => {
        if (!rawUserData || !filteredJobs?.length) return
        const chOrValues = {}
        const dayZeroValues = {}
        const mechanicallyCompleteDates = {}
        const startDates = {}
        const completionDates = {}
        const physicalComplete = {}
        const phasedEquipment = {}
        const phasedLabor = {}
        const laborValues = {}
        const equipmentValues = {}
        const contingData = {}
        const etcValues = {}
        const etcValuesPieces = {}
        const phaseEtcValues = {}
        const phaseEtcValuesPieces = {}
        const summaryEtcVals = {}
        const jobIds = filteredJobs.map(j => j.BU_ID)

        rawUserData.forEach((d) => {
            if (d?.Forecast_Tab === 'Change Order') {
                if (chOrValues[d?.BU_ID]) {
                    chOrValues[d?.BU_ID][d?.Forecast_User_Data_GUID || d?.Forecast_Row_ID] = { ...d, [d.Cost_Type]: d.Value_Amount, id: d.Row_Label }
                } else {
                    chOrValues[d?.BU_ID] = {
                        [d?.Forecast_User_Data_GUID || d?.Forecast_Row_ID]: { ...d, [d.Cost_Type]: d.Value_Amount, id: d.Row_Label }
                    }
                }
            }

            if (d?.Forecast_Tab === 'Day Zero Budget') {
                if (dayZeroValues[d?.BU_ID] && dayZeroValues[d?.BU_ID][d?.Forecast_Row_ID]) {
                    dayZeroValues[d?.BU_ID][d?.Forecast_Row_ID] = d
                } else if (dayZeroValues[d?.BU_ID]) {
                    dayZeroValues[d?.BU_ID][d?.Forecast_Row_ID] = d
                } else {
                    dayZeroValues[d?.BU_ID] = {
                        [d?.Forecast_Row_ID]: d
                    }
                }
            }

            if (d?.Forecast_Tab === 'All' || d?.Forecast_Tab === 'Forecast' || d?.Forecast_Tab === 'Set Dates') {
                if (d?.Forecast_Field_Name === 'mechanicallyComplete') {
                    mechanicallyCompleteDates[d?.BU_ID] = d?.Forecast_Value ? moment(d?.Forecast_Value).format('M/D/YYYY') : ''
                } else if (d?.Forecast_Field_Name === 'startDateActual') {
                    startDates[d?.BU_ID] = d?.Forecast_Value ? moment(d?.Forecast_Value).format('M/D/YYYY') : ''
                } else if (d?.Forecast_Field_Name === 'completionDateForecast') {
                    completionDates[d?.BU_ID] = d?.Forecast_Value ? moment(d?.Forecast_Value).format('M/D/YYYY') : ''
                } else if (d?.Forecast_Field_Name === 'physicalComplete') {
                    physicalComplete[d?.BU_ID] = d?.Forecast_Value
                }
            }

            if (d?.Forecast_Tab === 'Burn Tool Labor') {
                if (laborValues[d?.BU_ID]) {
                    laborValues[d?.BU_ID][d?.Forecast_Row_ID] = d
                } else {
                    laborValues[d?.BU_ID] = {
                        [d?.Forecast_Row_ID]: d
                    }
                }
            }

            if (d?.Forecast_Tab === 'Burn Tool Equipment') {
                if (equipmentValues[d?.BU_ID]) {
                    equipmentValues[d?.BU_ID][d?.Forecast_Row_ID] = d
                } else {
                    equipmentValues[d?.BU_ID] = {
                        [d?.Forecast_Row_ID]: d
                    }
                }
            }

            if (d?.Forecast_Tab === 'Burn Tool Equipment Phased') {
                if (phasedEquipment[d?.BU_ID]?.[d?.Forecast_Row_ID]) {
                    phasedEquipment[d?.BU_ID][d?.Forecast_Row_ID] = {
                        id: d?.Forecast_Row_ID,
                        ...d
                    }
                } else if (phasedEquipment[d?.BU_ID]) {
                    phasedEquipment[d?.BU_ID][d?.Forecast_Row_ID] = {
                        id: d?.Forecast_Row_ID,
                        ...d
                    }
                } else {
                    phasedEquipment[d?.BU_ID] = {
                        [d?.Forecast_Row_ID]: {
                            id: d?.Forecast_Row_ID,
                            ...d
                        }
                    }
                }
            }

            if (d?.Forecast_Tab === 'Burn Tool Labor Phased') {
                if (phasedLabor[d?.BU_ID]?.[d?.Forecast_Row_ID]) {
                    phasedLabor[d?.BU_ID][d?.Forecast_Row_ID] = {
                        id: d?.Forecast_Row_ID,
                        ...d
                    }
                } else if (phasedLabor[d?.BU_ID]) {
                    phasedLabor[d?.BU_ID][d?.Forecast_Row_ID] = {
                        id: d?.Forecast_Row_ID,
                        ...d
                    }
                } else {
                    phasedLabor[d?.BU_ID] = {
                        [d?.Forecast_Row_ID]: {
                            id: d?.Forecast_Row_ID,
                            ...d
                        }
                    }
                }
            }

            if (d?.Forecast_Tab === 'Contingency') {
                if (contingData[d?.BU_ID]?.[d?.Forecast_Row_ID]) {
                    contingData[d?.BU_ID][d?.Forecast_Row_ID][d.Forecast_Field_Name] = d.Forecast_Value
                } else if (contingData[d?.BU_ID]) {
                    contingData[d?.BU_ID][d?.Forecast_Row_ID] = {
                        id: d?.Forecast_Row_ID,
                        [d.Forecast_Field_Name]: d.Forecast_Value
                    }
                } else {
                    contingData[d?.BU_ID] = {
                        [d?.Forecast_Row_ID]: {
                            id: d?.Forecast_Row_ID,
                            [d.Forecast_Field_Name]: d.Forecast_Value
                        }
                    }
                }
            }

            // Match bidForecast: count every saved ETC row regardless of which tab wrote it
            // (All Third Party, Forecast grid, etc.) — a hardcoded tab list silently drops those.
            // Field Bonus (51800) is retired — exclude forecast ETC 51800 from BOTH the on-screen
            // ETC/EAC total and the exported pieces so screen and CSV/Excel stay in lockstep.
            // (Actual/committed 51800 still flows through as real spent cost.)
            if (d?.Cost_Type && d?.Cost_Type != '51800' && d?.Forecast_Field_Name == 'etc' && (!d?.Cost_Code || d?.Cost_Code != '101101')) {
                if (d?.Cost_Type == 'undefined') {
                    d.Cost_Type = '100105'
                }
                if (etcValues[d?.BU_ID]?.[d?.Cost_Type]) {
                    etcValues[d?.BU_ID][d?.Cost_Type] = etcValues[d?.BU_ID][d?.Cost_Type] + pNum(d?.Value_Amount)
                } else if (etcValues[d?.BU_ID]) {
                    etcValues[d?.BU_ID][d?.Cost_Type] = pNum(d?.Value_Amount)
                } else {
                    etcValues[d?.BU_ID] = {
                        [d?.Cost_Type]: pNum(d?.Value_Amount),
                    }
                }

                if (!d?.Cost_Code || d?.Cost_Code?.includes('^')) d.Cost_Code = '100105'
                // Pieces must carry only the cost types the displayed ETC total counts
                // (51600-51899, 52x, 53x, 54x, 55x-57x — see etcTotal below). Rows outside
                // those buckets aren't in the Fusion EAC, so exporting them would make the
                // Excel/CSV disagree with the screen.
                const ct = d.Cost_Type
                const inEtcBuckets = ct?.[0] == '5' && (
                    (ct[1] == '1' && (ct[2] == '6' || ct[2] == '7' || ct[2] == '8')) ||
                    ct[1] == '2' || ct[1] == '3' || ct[1] == '4' ||
                    ct[1] == '5' || ct[1] == '6' || ct[1] == '7'
                )
                if (inEtcBuckets) {
                    if (!etcValuesPieces[d?.BU_ID]) {
                        etcValuesPieces[d?.BU_ID] = {}
                    }
                    if (!etcValuesPieces[d?.BU_ID][d?.Cost_Type]) {
                        etcValuesPieces[d?.BU_ID][d?.Cost_Type] = {}
                    }
                    if (!etcValuesPieces[d?.BU_ID][d?.Cost_Type][d?.Cost_Code]) {
                        etcValuesPieces[d?.BU_ID][d?.Cost_Type][d?.Cost_Code] = 0
                    }
                    etcValuesPieces[d?.BU_ID][d?.Cost_Type][d?.Cost_Code] = etcValuesPieces[d?.BU_ID][d?.Cost_Type][d?.Cost_Code] + pNum(d?.Value_Amount)
                }
            }

            // Field Bonus (51800) retired — keep it out of the phased ETC comparison too.
            if (d?.Cost_Type && d?.Cost_Type != '51800' && d?.Forecast_Field_Name == 'etc' && (!d?.Cost_Code || d?.Cost_Code != '101101')) {
                if (!d?.Cost_Code) {
                    d.Cost_Code = '100105'
                }
                if (phaseEtcValues[d?.BU_ID]?.[d?.Cost_Type]?.[d?.Cost_Code]) {
                    phaseEtcValues[d?.BU_ID][d?.Cost_Type][d?.Cost_Code] = phaseEtcValues[d?.BU_ID][d?.Cost_Type][d?.Cost_Code] + pNum(d?.Value_Amount)
                } else if (phaseEtcValues[d?.BU_ID]?.[d?.Cost_Type]) {
                    phaseEtcValues[d?.BU_ID][d?.Cost_Type][d?.Cost_Code] = pNum(d?.Value_Amount)
                } else if (phaseEtcValues[d?.BU_ID]?.[d?.Cost_Type]) {
                    phaseEtcValues[d?.BU_ID][d?.Cost_Type] = {}
                    phaseEtcValues[d?.BU_ID][d?.Cost_Type][d?.Cost_Code] = pNum(d?.Value_Amount)
                } else {
                    phaseEtcValues[d?.BU_ID] = {}
                    phaseEtcValues[d?.BU_ID][d?.Cost_Type] = {}
                    phaseEtcValues[d?.BU_ID][d?.Cost_Type][d?.Cost_Code] = pNum(d?.Value_Amount)
                }

                if (!phaseEtcValuesPieces[d?.BU_ID]) {
                    phaseEtcValuesPieces[d?.BU_ID] = {}
                }
                if (!phaseEtcValuesPieces[d?.BU_ID][d?.Cost_Type]) {
                    phaseEtcValuesPieces[d?.BU_ID][d?.Cost_Type] = {}
                }
                if (!phaseEtcValuesPieces[d?.BU_ID][d?.Cost_Type][d?.Cost_Code]) {
                    phaseEtcValuesPieces[d?.BU_ID][d?.Cost_Type][d?.Cost_Code] = 0
                }
                phaseEtcValuesPieces[d?.BU_ID][d?.Cost_Type][d?.Cost_Code] = phaseEtcValuesPieces[d?.BU_ID][d?.Cost_Type][d?.Cost_Code] + pNum(d?.Value_Amount)
            }

            // Summary-style ETC (mirrors summary.js's selection) — Field Bonus (51800) retired.
            if (d?.Cost_Type && d?.Cost_Type != '51800' && d?.Forecast_Field_Name == 'etc' && (!d?.Cost_Code || d?.Cost_Code != '101101')) {
                if (summaryEtcVals[d?.BU_ID]?.[d?.Cost_Type]) {
                    summaryEtcVals[d?.BU_ID][d?.Cost_Type] = summaryEtcVals[d?.BU_ID][d?.Cost_Type] + pNum(d?.Value_Amount)
                } else if (summaryEtcVals[d?.BU_ID]) {
                    summaryEtcVals[d?.BU_ID][d?.Cost_Type] = pNum(d?.Value_Amount)
                } else {
                    summaryEtcVals[d?.BU_ID] = { [d?.Cost_Type]: pNum(d?.Value_Amount) }
                }
            }
        })
        setEquipmentUserData(phasedEquipment)
        setSummaryETCValues(summaryEtcVals)

        let etcLabor = {}
        let etcLaborTwo = {}
        let etcIncentive = {}
        let etcEquipment = {}
        let etcMaterials = {}
        let etcRentals = {}
        let etcSubcontractors = {}
        let etcOther = {}
        let etcContingency = {}
        let totalContingency = 0
        let etcTotal = {}
        let hours = {}

        let equipmentCostTypePieces = {}
        let laborCostTypePieces = {}
        let regularTimePieces = {}

        // Fall back to Planned_Complete_Date for any job missing a saved completionDateForecast
        // Must run BEFORE the labor/equipment loop below — otherwise pC is undefined and
        // moment(undefined).isAfter(week) silently drops every future week.
        filteredJobs.forEach((job) => {
            if (!completionDates[job.BU_ID] && job?.Planned_Complete_Date) {
                completionDates[job.BU_ID] = moment(job.Planned_Complete_Date).format('M/D/YYYY')
            }
        })

        const contingencyPiecesLocal = {}
        jobIds.forEach((job) => {
            let regularTimeCostType = 0
            let equipmentCostTypeVal = 0
            let regularTime = 0
            let regularHours = 0
            let etc = 0
            const pC = completionDates[job]

            equipmentValues[job] && Object.keys(equipmentValues[job]).forEach((key) => {
                if (key.length == 10 && moment(key).isAfter(moment(versionInfo?.Version_Start_DateTime).startOf('week')) && moment(pC).endOf('week').isAfter(key)) {
                    const row = equipmentValues[job]?.[key]
                    equipmentCostTypeVal = equipmentCostTypeVal + ((isNaN(pNum(row.Rate)) ? 0 : pNum(row.Rate)) * (isNaN(pNum(row.Number_Units)) ? 0 : pNum(row.Number_Units)) * (isNaN(pNum(row.Number_Hours)) ? 0 : pNum(row.Number_Hours)))
                    const costCode = row.Cost_Code && row.Cost_Code != 'undefined' ? row.Cost_Code : '100105'
                    if (!equipmentCostTypePieces[job]) {
                        equipmentCostTypePieces[job] = {}
                    }
                    if (!equipmentCostTypePieces[job][row.Cost_Type]) {
                        equipmentCostTypePieces[job][row.Cost_Type] = {}
                    }
                    if (!equipmentCostTypePieces[job][row.Cost_Type][costCode]) {
                        equipmentCostTypePieces[job][row.Cost_Type][costCode] = 0
                    }
                    equipmentCostTypePieces[job][row.Cost_Type][costCode] = equipmentCostTypePieces[job][row.Cost_Type][costCode] + (isNaN(pNum(row.Rate)) ? 0 : pNum(row.Rate)) * (isNaN(pNum(row.Number_Units)) ? 0 : pNum(row.Number_Units)) * (isNaN(pNum(row.Number_Hours)) ? 0 : pNum(row.Number_Hours))
                }
            })

            laborValues[job] && Object.values(laborValues[job]).forEach((row) => {
                if (row) {
                    const costCode = row.Cost_Code && row.Cost_Code != 'undefined' ? row.Cost_Code : '100105'
                    const week = row?.Forecast_Row_ID ? row.Forecast_Row_ID.split('*')[0] : ''
                    const pC = completionDates[job]
                    if (week && moment(week).isAfter(moment(versionInfo?.Version_Start_DateTime).startOf('week')) && moment(pC).isAfter(week)) {
                        if (!laborCostTypePieces[job]) {
                            laborCostTypePieces[job] = {
                                '51100': {
                                    [costCode]: 0
                                }
                            }
                        }
                        if (!laborCostTypePieces[job]['51100'][costCode]) {
                            laborCostTypePieces[job]['51100'][costCode] = 0
                        }
                        laborCostTypePieces[job]['51100'][costCode] = laborCostTypePieces[job]['51100'][costCode] + (!isNaN(pNum(row.Rate)) && !isNaN(pNum(row.Number_Units)) && !isNaN(pNum(row.Number_Hours)) ? pNum(row.Rate) * pNum(row.Number_Units) * pNum(row.Number_Hours) : 0)
                        regularTimeCostType = regularTimeCostType + (!isNaN(pNum(row.Rate)) && !isNaN(pNum(row.Number_Units)) && !isNaN(pNum(row.Number_Hours)) ? pNum(row.Rate) * pNum(row.Number_Units) * pNum(row.Number_Hours) : 0)
                        regularHours = regularHours + (!isNaN(pNum(row.Number_Hours)) && !isNaN(pNum(row.Number_Units)) ? pNum(row.Number_Hours) * pNum(row.Number_Units) : 0)
                    }
                }
            })

            phasedLabor[job] && Object.keys(phasedLabor[job]).forEach((row) => {
                const weekRow = row.includes('=')
                const week = row.split('=')[1]
                const phase = row.split('=')[0]
                const pC = completionDates[job]
                const withinProject = moment(week).isAfter(moment(versionInfo.Version_Start_DateTime).startOf('week')) && moment(week).isBefore(moment(pC).endOf('week'))
                const withinStartStop = true
                const h = phasedLabor[job][row]?.Number_Hours ? phasedLabor[job][row].Number_Hours : 0
                const bR = phasedLabor[job][row]?.Rate ? phasedLabor[job][row].Rate : 0
                const cS = phasedLabor[job][row]?.Number_Units ? phasedLabor[job][row].Number_Units : 0
                if (weekRow && phasedLabor[job][row] && h && bR && cS && withinProject && withinStartStop) {
                    if (!regularTimePieces[job]) {
                        regularTimePieces[job] = {
                            ['51100']: {
                                [phase]: 0
                            }
                        }
                    }
                    if (!regularTimePieces[job]['51100'][phase]) {
                        regularTimePieces[job]['51100'][phase] = 0
                    }
                    regularTimePieces[job]['51100'][phase] = regularTimePieces[job]['51100'][phase] + (!isNaN(pNum(bR)) && !isNaN(pNum(h)) && !isNaN(pNum(cS)) ? pNum(bR) * pNum(h) * pNum(cS) : 0)
                    regularHours = regularHours + (!isNaN(pNum(h)) && !isNaN(pNum(cS)) ? pNum(h) * pNum(cS) : 0)
                    regularTime = regularTime + (!isNaN(pNum(h)) && !isNaN(pNum(bR)) && !isNaN(pNum(cS)) ? pNum(h) * pNum(bR) * pNum(cS) : 0)
                }
            })

            let laborVal = 0
            let materialsVal = 0
            let rentalsVal = 0
            let subcontractorsVal = 0
            let otherVal = 0

            etcValues[job] && Object.keys(etcValues[job]).forEach((row) => {
                etc = etc + (isNaN(pNum(etcValues[job][row])) ? 0 : pNum(etcValues[job][row]))
                if (row[0] == '5' && row[1] == '1' && (row[2] == '6' || row[2] == '7' || row[2] == '8')) {
                    laborVal = laborVal + (isNaN(pNum(etcValues[job][row])) ? 0 : pNum(etcValues[job][row]))
                } else if (row[0] == '5' && row[1] == '2') {
                    rentalsVal = rentalsVal + (isNaN(pNum(etcValues[job][row])) ? 0 : pNum(etcValues[job][row]))
                } else if (row[0] == '5' && row[1] == '3') {
                    materialsVal = materialsVal + (isNaN(pNum(etcValues[job][row])) ? 0 : pNum(etcValues[job][row]))
                } else if (row[0] == '5' && row[1] == '4') {
                    subcontractorsVal = subcontractorsVal + (isNaN(pNum(etcValues[job][row])) ? 0 : pNum(etcValues[job][row]))
                } else if (row[0] == '5' && (row[1] == '5' || row[1] == '6' || row[1] == '7')) {
                    otherVal = otherVal + (isNaN(pNum(etcValues[job][row])) ? 0 : pNum(etcValues[job][row]))
                }
            })

            let totalcLabor = 0
            let totalcEquipment = 0
            let totalcMaterials = 0
            let totalcSubcontractors = 0
            let totalcOtherExpenses = 0

            contingencyPiecesLocal[job] = {
                '51100': { '101101': 0 },
                '52100': { '101101': 0 },
                '53100': { '101101': 0 },
                '54200': { '101101': 0 },
                '57900': { '101101': 0 }
            }

            contingData[job] && Object.values(contingData[job]).forEach((row) => {
                totalcLabor = totalcLabor + (isNaN(pNum(row.CurrentLabor)) ? 0 : pNum(row.CurrentLabor))
                contingencyPiecesLocal[job]['51100']['101101'] = contingencyPiecesLocal[job]['51100']['101101'] + (isNaN(pNum(row.CurrentLabor)) ? 0 : pNum(row.CurrentLabor))
                totalcEquipment = totalcEquipment + (isNaN(pNum(row.CurrentEquipment)) ? 0 : pNum(row.CurrentEquipment))
                contingencyPiecesLocal[job]['52100']['101101'] = contingencyPiecesLocal[job]['52100']['101101'] + (isNaN(pNum(row.CurrentEquipment)) ? 0 : pNum(row.CurrentEquipment))
                totalcMaterials = totalcMaterials + (isNaN(pNum(row.CurrentMaterials)) ? 0 : pNum(row.CurrentMaterials))
                contingencyPiecesLocal[job]['53100']['101101'] = contingencyPiecesLocal[job]['53100']['101101'] + (isNaN(pNum(row.CurrentMaterials)) ? 0 : pNum(row.CurrentMaterials))
                totalcSubcontractors = totalcSubcontractors + (isNaN(pNum(row.CurrentSubs)) ? 0 : pNum(row.CurrentSubs))
                contingencyPiecesLocal[job]['54200']['101101'] = contingencyPiecesLocal[job]['54200']['101101'] + (isNaN(pNum(row.CurrentSubs)) ? 0 : pNum(row.CurrentSubs))
                totalcOtherExpenses = totalcOtherExpenses + (isNaN(pNum(row.CurrentOther)) ? 0 : pNum(row.CurrentOther))
                contingencyPiecesLocal[job]['57900']['101101'] = contingencyPiecesLocal[job]['57900']['101101'] + (isNaN(pNum(row.CurrentOther)) ? 0 : pNum(row.CurrentOther))
            })
            let totalcTotal = totalcLabor + totalcEquipment + totalcMaterials + totalcSubcontractors + totalcOtherExpenses

            hours[job] = regularHours
            etcLabor[job] = laborVal + regularTimeCostType + regularTime
            etcLaborTwo[job] = regularTimeCostType
            etcIncentive[job] = laborVal
            etcEquipment[job] = equipmentCostTypeVal
            etcMaterials[job] = materialsVal
            etcRentals[job] = rentalsVal
            etcSubcontractors[job] = subcontractorsVal
            etcOther[job] = otherVal
            etcContingency[job] = totalcTotal
            totalContingency = totalContingency + totalcTotal
            etcTotal[job] = laborVal + regularTimeCostType + equipmentCostTypeVal + materialsVal + rentalsVal + subcontractorsVal + otherVal + totalcTotal
        })

        setContingencyPieces(contingencyPiecesLocal)
        setHoursETC(hours)
        setLabor(etcLabor)
        setLaborCostType(etcLaborTwo)
        setEquipmentCostType(etcEquipment)
        setMaterials(etcMaterials)
        setRentals(etcRentals)
        setSubcontractors(etcSubcontractors)
        setOther(etcOther)
        setContingency(etcContingency)
        setIncentive(etcIncentive)
        setTotal(etcTotal)
        setETC(etcTotal)

        setEtcPieces(etcValuesPieces)
        setLaborPieces(laborCostTypePieces)
        setLaborPhasedPieces(regularTimePieces)
        setEquipmentPieces(equipmentCostTypePieces)

        let curBudgetLocal = {}
        let totalBudgetLocal = 0
        filteredJobs.forEach((job) => {
            let jobCurBudget = 0
            dayZeroValues[job.BU_ID] && Object.values(dayZeroValues[job.BU_ID]).forEach((row) => {
                if (row['Cost_Type'] && row['Cost_Type'].length) {
                    if (row['Cost_Type'][0] == '4') {
                        jobCurBudget = jobCurBudget + pNum(row['Value_Amount'])
                    }
                }
            })
            chOrValues[job.BU_ID] && Object.values(chOrValues[job.BU_ID]).forEach((row) => {
                if (row.Status === 'Approved') {
                    jobCurBudget = jobCurBudget + (isNaN(pNum(row['41000'])) ? 0 : pNum(row['41000']))
                }
            })
            curBudgetLocal[job.BU_ID] = jobCurBudget > 0 ? fNum(jobCurBudget, 1) : ''
            totalBudgetLocal = totalBudgetLocal + (jobCurBudget > 0 ? jobCurBudget : 0)
        })
        setCurBudget(curBudgetLocal)
        setTotalBudget(fNum(totalBudgetLocal, 1))
        setDayZeroData(dayZeroValues)
        setCompletionDates(completionDates)
        setPhysicalComplete(physicalComplete)
    }, [rawUserData, filteredJobs])

    // ---- Phased equipment ETC ----
    useEffect(() => {
        const phasedEquipmentCost = {}
        const equipmentPiecesLocal = {}
        const jobIds = filteredJobs.map(j => j.BU_ID)
        const equipmentRates = {}
        jobIds.forEach((job) => {
            equipmentUserData[job] && Object.keys(equipmentUserData[job]).forEach((row) => {
                if (equipmentUserData[job]?.[row]?.weekPerUnit) {
                    equipmentRates[equipmentUserData[job][row].equipment] = equipmentUserData[job][row].weekPerUnit
                }
            })
        })
        jobIds.forEach((job) => {
            let equipmentETC = 0
            equipmentUserData[job] && Object.keys(equipmentUserData[job]).forEach((t) => {
                const r = equipmentUserData[job][t]
                const pC = completionDates[job]
                const v = versionInfo?.Version_Start_DateTime
                // Row IDs use '=' (new) or '*' (legacy) between name and week
                const w = t.includes('=') ? t.split('=').pop() : t.split('*')[1]
                const rate = r?.Rate
                const h = r?.Number_Hours
                const n = r?.Number_Units
                if (w && h && n && rate && moment(w).isAfter(moment(v).startOf('week')) && moment(w).isBefore(pC)) {
                    if (!equipmentPiecesLocal[job]) {
                        equipmentPiecesLocal[job] = {
                            '52100': {
                                [r.phaseCode]: 0
                            }
                        }
                    }
                    if (!equipmentPiecesLocal[job]['52100'][r.phaseCode]) {
                        equipmentPiecesLocal[job]['52100'][r.phaseCode] = 0
                    }
                    equipmentPiecesLocal[job]['52100'][r.phaseCode] = equipmentPiecesLocal[job]['52100'][r.phaseCode] + (!isNaN(pNum(rate)) && !isNaN(pNum(h)) && !isNaN(pNum(n)) ? pNum(rate) * pNum(h) * pNum(n) : 0)
                    equipmentETC = equipmentETC + (!isNaN(pNum(rate)) && !isNaN(pNum(h)) && !isNaN(pNum(n)) ? pNum(rate) * pNum(h) * pNum(n) : 0)
                }
            })
            phasedEquipmentCost[job] = equipmentETC
        })
        setEquipmentPhased(phasedEquipmentCost)
        setEquipmentPhasedPieces(equipmentPiecesLocal)
    }, [equipmentUserData, equipRates, completionDates, filteredJobs])

    // ---- ETC recalc when phased equipment changes ----
    useEffect(() => {
        const jobIds = filteredJobs.map(j => j.BU_ID)
        const etc = {}
        jobIds.forEach((job) => {
            let etcTotal = labor[job] + materials[job] + rentals[job] + subcontractors[job] + other[job] + equipmentPhased[job] + equipmentCostType[job] + contingency[job]
            etc[job] = etcTotal
        })
        setETC(etc)
        setTotal(etc)
    }, [equipmentPhased, equipmentCostType, contingency])

    // ---- EAC / Job Profit / Gross Margin ----
    useEffect(() => {
        let eacLocal = {}
        let jobProfitLocal = {}
        let jobProfitPercLocal = {}
        let jobGrossMargin = {}
        let jobGrossMarginPerc = {}
        let backlogLocal = {}
        let costPercentLocal = {}
        let earnedRevenueLocal = {}

        filteredJobs.forEach((job) => {
            const totalVal = (pNum(actualCost[job.BU_ID]) + pNum(openCommitments[job.BU_ID]) + pNum(ETC[job.BU_ID]))
            eacLocal[job.BU_ID] = totalVal > 0 ? fNum(totalVal, 1) : ''
            jobProfitLocal[job.BU_ID] = totalVal > 0 && curBudget[job.BU_ID] ? fNum(pNum(curBudget[job.BU_ID]) - totalVal, 1) : ''
            jobProfitPercLocal[job.BU_ID] = jobProfitLocal[job.BU_ID] ? fNum(pNum(jobProfitLocal[job.BU_ID]) / pNum(curBudget[job.BU_ID]) * 100) : ''

            const gm = (pNum(curBudget[job.BU_ID]) - totalVal - (isNaN(pNum(actualOverhead[job.BU_ID])) ? 0 : pNum(actualOverhead[job.BU_ID]))) - (isNaN(pNum(etcOverhead[job.BU_ID])) ? 0 : pNum(etcOverhead[job.BU_ID]))
            jobGrossMargin[job.BU_ID] = gm
            jobGrossMarginPerc[job.BU_ID] = gm && curBudget[job.BU_ID] ? fNum(gm / pNum(curBudget[job.BU_ID]) * 100) : ''

            backlogLocal[job.BU_ID] = (1 - (pNum(actualCost[job.BU_ID]) / totalVal)) * pNum(curBudget[job.BU_ID])
            costPercentLocal[job.BU_ID] = totalVal ? (pNum(actualCost[job.BU_ID]) / totalVal) * 100 : ''
            earnedRevenueLocal[job.BU_ID] = (costPercentLocal[job.BU_ID] / 100) * pNum(curBudget[job.BU_ID])
        })
        setEAC(eacLocal)
        setJobProfit(jobProfitLocal)
        setJobProfitPerc(jobProfitPercLocal)
        setGrossMargin(jobGrossMargin)
        setGrossMarginPerc(jobGrossMarginPerc)
        setBacklog(backlogLocal)
        setCostPercent(costPercentLocal)
        setEarnedRevenue(earnedRevenueLocal)
    }, [openCommitments, actualCost, ETC, curBudget, totalBudget])

    // ---- Build projection rows (same as runProjections) ----
    useEffect(() => {
        let uniqueCombinations = new Map();

        const addToTotals = (jobId, costType, phaseCode, value, source) => {
            if (costType?.startsWith("51800") && source !== "actuals" && source !== "openCommitments") {
                phaseCode = "100105";
            }
            if (costType === "52400" && phaseCode === "100105") {
                phaseCode = "100102";
            }
            if (costType === "54200" && phaseCode === "100105") {
                phaseCode = "100101";
            }
            if ((costType === "51100" || costType === "52100") && (!phaseCode || phaseCode == 'undefined') && source !== "actuals" && source !== "openCommitments") {
                phaseCode = "100105";
            }
            if (phaseCode && phaseCode.includes('^')) {
                phaseCode = '100105'
            }
            const key = `${jobId}-${costType}-${phaseCode}`;
            if (uniqueCombinations.has(key)) {
                uniqueCombinations.set(key, value ? uniqueCombinations.get(key) + value : uniqueCombinations.get(key));
            } else {
                uniqueCombinations.set(key, value);
            }
        };

        const processDataset = (dataset, source) => {
            Object.keys(dataset).forEach((jobId) => {
                const job = dataset[jobId];
                Object.keys(job).forEach((costType) => {
                    Object.keys(job[costType]).forEach((phaseCode) => {
                        addToTotals(jobId, costType, phaseCode, pNum(job[costType][phaseCode]), source);
                    });
                });
            });
        };

        processDataset(openCommitmentsPieces, "openCommitments");
        processDataset(laborPieces, "labor");
        processDataset(laborPhasedPieces, "laborPhased");
        processDataset(equipmentPieces, "equipment");
        processDataset(equipmentPhasedPieces, "equipmentPhased");
        processDataset(contingencyPieces, "contingency");
        processDataset(etcPieces, "etc");

        projectionCost && projectionCost.forEach((job) => {
            addToTotals(job.Job_Number, job.Cost_Type_Code, job.Phase_Code, pNum(job.Total_Job_Cost), "actuals");
        });

        const tierSize = {}
        filteredJobs.forEach((job) => {
            if (job?.BU_ID) {
                tierSize[job.BU_ID] = job?.Cat_Code_BU_16
            }
        });
        const finalData = Array.from(uniqueCombinations, ([key, value]) => {
            const [jobId, costType, phaseCode] = key.split("-");
            // Keep the raw number — per-row fNum rounding made the exported sum drift
            // cents off the displayed EAC. Exports round at emit instead.
            return { jobId, costType, phaseCode, value: isNaN(value) ? 0 : value, tier: tierSize[jobId] };
        });

        filteredJobs.forEach((job) => {
            finalData.push({
                jobId: job.BU_ID,
                costType: '41000',
                phaseCode: '990000',
                value: curBudget[job.BU_ID] ? '-' + curBudget[job.BU_ID] : 0
            })
            finalData.push({
                jobId: job.BU_ID,
                costType: '60900',
                phaseCode: '1000',
                value: actualOverhead[job.BU_ID] && etcOverhead[job.BU_ID] ? pNum(actualOverhead[job.BU_ID]) + pNum(etcOverhead[job.BU_ID]) : actualOverhead[job.BU_ID] ? actualOverhead[job.BU_ID] : etcOverhead[job.BU_ID] ? etcOverhead[job.BU_ID] : 0
            })
        })

        setProjectionRows(finalData);
        setProjectionDataLoaded(true);
    }, [filteredJobs, openCommitmentsPieces, projectionCost, equipmentPieces, equipmentPhasedPieces, actualOverhead]);

    // ---- Fiscal Period Calculation (same as budgets.js) ----
    const buildPeriod = (targetDate) => {
        const targetMonth = targetDate.getMonth() + 1;
        const targetYear = targetDate.getFullYear();
        // JDE company constants (F0010 CCDFYJ) put the fiscal year start at Jan 1,
        // so FY = calendar year and period = calendar month (July 2026 = P07 FY26).
        const fiscalYear = targetYear;
        return {
            periodNbr: targetMonth.toString().padStart(2, '0'),
            fiscalYear: fiscalYear.toString(),
            fiscalYearShort: fiscalYear.toString().slice(-2),
            century: Math.floor(fiscalYear / 100).toString(),
            displayDate: targetDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
            key: `${targetYear}-${targetMonth.toString().padStart(2, '0')}`
        };
    };

    const calculateDefaultPeriod = () => {
        const today = new Date();
        const currentDay = today.getDate();
        const currentMonth = today.getMonth();
        const currentYear = today.getFullYear();
        let targetDate;
        if (currentDay <= 15) {
            targetDate = new Date(currentYear, currentMonth - 1, 1);
        } else {
            targetDate = new Date(currentYear, currentMonth, 1);
        }
        return buildPeriod(targetDate);
    };

    const getPeriodOptions = () => {
        const options = [];
        const today = new Date();
        for (let i = -3; i <= 3; i++) {
            const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
            options.push(buildPeriod(d));
        }
        return options;
    };

    const currentPeriod = syncPeriod || calculateDefaultPeriod();

    // ---- HA Comparison + Summary Fallback + Discrepancy Sorting ----
    useEffect(() => {
        if (!filteredJobs.length || !haDataLoaded || !projectionDataLoaded) return;

        // Build HA values per job from Fact_GL_Balances grouped data
        // Data format: { BU_ID, Account_Group, Total_Amount }
        // Account_Group: '41000'=revenue, '51000'=labor, '52000'=equipment, '53000'=materials,
        //                '54000'=subs, '55000'=other, '101101'=contingency, '60900'=overhead
        const haJobTotals = {};
        jdeValues && jdeValues.forEach((d) => {
            const jobId = d.BU_ID?.trim();
            const group = d.Account_Group?.trim();
            const amount = pNum(d.Total_Amount);
            if (!jobId || !group) return;

            if (!haJobTotals[jobId]) {
                haJobTotals[jobId] = { revenue: 0, labor: 0, equipment: 0, materials: 0, subs: 0, other: 0, contingency: 0, overhead: 0, total: 0 };
            }

            if (group === '41000') haJobTotals[jobId].revenue += amount;
            else if (group === '101101') haJobTotals[jobId].contingency += amount;
            else if (group === '51000') haJobTotals[jobId].labor += amount;
            else if (group === '52000') haJobTotals[jobId].equipment += amount;
            else if (group === '53000') haJobTotals[jobId].materials += amount;
            else if (group === '54000') haJobTotals[jobId].subs += amount;
            else if (group === '55000') haJobTotals[jobId].other += amount;
            else if (group === '60900') haJobTotals[jobId].overhead += amount;
        });

        Object.keys(haJobTotals).forEach(jobId => {
            const h = haJobTotals[jobId];
            // Revenue stored negative in JDE — negate to get positive
            h.revenue = -1 * h.revenue;
            h.total = h.labor + h.equipment + h.materials + h.subs + h.other + h.contingency;
        });

        // Build our projection values per job (from projectionRows)
        const ourJobTotals = {};
        projectionRows.forEach((row) => {
            const { jobId, costType, phaseCode, value } = row;
            if (!jobId || !costType) return;
            const amount = pNum(value);

            if (!ourJobTotals[jobId]) {
                ourJobTotals[jobId] = { revenue: 0, labor: 0, equipment: 0, materials: 0, subs: 0, other: 0, contingency: 0, overhead: 0, total: 0 };
            }

            if (costType.startsWith('41')) ourJobTotals[jobId].revenue += amount;
            else if (phaseCode === '101101') ourJobTotals[jobId].contingency += amount;
            else if (costType.startsWith('51')) ourJobTotals[jobId].labor += amount;
            else if (costType.startsWith('52')) ourJobTotals[jobId].equipment += amount;
            else if (costType.startsWith('53')) ourJobTotals[jobId].materials += amount;
            else if (costType.startsWith('54')) ourJobTotals[jobId].subs += amount;
            else if (costType.startsWith('55') || costType.startsWith('56') || costType.startsWith('57')) ourJobTotals[jobId].other += amount;
            else if (costType.startsWith('60')) ourJobTotals[jobId].overhead += amount;
        });

        Object.keys(ourJobTotals).forEach(jobId => {
            const o = ourJobTotals[jobId];
            o.total = o.labor + o.equipment + o.materials + o.subs + o.other + o.contingency;
        });

        // Build comparison per job
        const comparison = {};
        filteredJobs.forEach((job) => {
            const jobId = job.BU_ID;
            const hasHA = haJobTotals[jobId] && (haJobTotals[jobId].total !== 0 || haJobTotals[jobId].revenue !== 0);
            const ours = ourJobTotals[jobId] || { revenue: 0, total: 0, labor: 0, equipment: 0, materials: 0, subs: 0, other: 0, contingency: 0, overhead: 0 };

            // Use displayed values directly (curBudget for revenue, EAC for cost)
            const ourRevenue = pNum(curBudget[jobId]);
            const ourCost = pNum(EAC[jobId]);

            // Compute Fusion Summary cost: use actual Summary component EAC/curBudget when available
            let fusionSummaryCost, fusionSummaryRevenue, etcDiff;
            if (summaryData && summaryData.EAC && summaryData.curBudget) {
                fusionSummaryCost = pNum(summaryData.EAC[jobId]);
                fusionSummaryRevenue = pNum(summaryData.curBudget[jobId]);
                etcDiff = Math.round(Math.abs(ourCost - fusionSummaryCost) * 100) / 100;
            } else {
                // Fallback: approximate using summaryETCValues (legacy behavior)
                let projSubsetETC = 0;
                const ourPieces = etcPieces[jobId] || {};
                Object.keys(ourPieces).forEach(ct => {
                    Object.keys(ourPieces[ct]).forEach(pc => {
                        projSubsetETC += pNum(ourPieces[ct][pc]);
                    });
                });

                let summarySubsetETC = 0;
                const sumPieces = summaryETCValues[jobId] || {};
                Object.keys(sumPieces).forEach(ct => {
                    summarySubsetETC += pNum(sumPieces[ct]);
                });

                fusionSummaryCost = ourCost + (summarySubsetETC - projSubsetETC);
                fusionSummaryRevenue = ourRevenue;
                etcDiff = Math.round(Math.abs(projSubsetETC - summarySubsetETC) * 100) / 100;
            }

            const ourOverhead = pNum(actualOverhead[jobId] || 0) + pNum(etcOverhead[jobId] || 0);

            if (hasHA) {
                // HA comparison: compare displayed Fusion values vs JDE HA values
                const ha = haJobTotals[jobId];
                const costDiff = Math.round(Math.abs(ourCost - ha.total) * 100) / 100;
                const revDiff = Math.round(Math.abs(ourRevenue - Math.abs(ha.revenue)) * 100) / 100;
                const ohDiff = Math.round(Math.abs(ourOverhead - ha.overhead) * 100) / 100;
                const totalDiff = costDiff + revDiff;

                comparison[jobId] = {
                    source: 'HA',
                    inSync: totalDiff < 1,
                    totalDiff,
                    costDiff,
                    revenueDiff: revDiff,
                    ourCost,
                    compareCost: ha.total,
                    ourRevenue,
                    compareRevenue: Math.abs(ha.revenue),
                    ourOverhead,
                    compareOverhead: ha.overhead,
                    overheadDiff: ohDiff,
                    summaryCost: fusionSummaryCost,
                    summaryRevenue: fusionSummaryRevenue,
                    summaryCostDiff: etcDiff,
                    summaryRevDiff: 0,
                    details: {
                        labor: { ours: ours.labor, compare: ha.labor, diff: Math.abs(ours.labor - ha.labor) },
                        equipment: { ours: ours.equipment, compare: ha.equipment, diff: Math.abs(ours.equipment - ha.equipment) },
                        materials: { ours: ours.materials, compare: ha.materials, diff: Math.abs(ours.materials - ha.materials) },
                        subs: { ours: ours.subs, compare: ha.subs, diff: Math.abs(ours.subs - ha.subs) },
                        other: { ours: ours.other, compare: ha.other, diff: Math.abs(ours.other - ha.other) },
                        contingency: { ours: ours.contingency, compare: ha.contingency, diff: Math.abs(ours.contingency - ha.contingency) },
                    }
                };
            } else {
                // No HA data: use summary comparison for sync status
                comparison[jobId] = {
                    source: 'Summary',
                    inSync: etcDiff < 1,
                    totalDiff: etcDiff,
                    costDiff: etcDiff,
                    revenueDiff: 0,
                    ourCost,
                    compareCost: fusionSummaryCost,
                    ourRevenue,
                    compareRevenue: ourRevenue,
                    ourOverhead,
                    compareOverhead: 0,
                    overheadDiff: 0,
                    summaryCost: fusionSummaryCost,
                    summaryRevenue: fusionSummaryRevenue,
                    summaryCostDiff: etcDiff,
                    summaryRevDiff: 0,
                };
            }
        });

        setJobComparison(comparison);

        // Sort: discrepancies first (largest diff), then in-sync jobs
        const sorted = [...filteredJobs].sort((a, b) => {
            const aComp = comparison[a.BU_ID] || { inSync: true, totalDiff: 0 };
            const bComp = comparison[b.BU_ID] || { inSync: true, totalDiff: 0 };
            if (aComp.inSync !== bComp.inSync) return aComp.inSync ? 1 : -1;
            return bComp.totalDiff - aComp.totalDiff;
        });
        setSortedFilteredJobs(sorted);
    }, [jdeValues, filteredJobs, summaryETCValues, summaryData, etcPieces, EAC, curBudget, projectionRows, haDataLoaded, projectionDataLoaded, actualOverhead, etcOverhead]);

    // ---- Export Projections Excel (same as runProjections) ----
    const exportCurrentValues = () => {
        // Aggregate by (jobId, costType, phaseCode) — match CSV/JDE upload behavior
        const aggregated = aggregateEACRows(exportProjectionRows).map(r => ({
            ...r,
            value: Math.round(r.value * 100) / 100
        }));

        // Reconciliation: per-job sums of the exported rows vs the values shown on the
        // Projection Sync screen. Cost = 5xxxx rows (what EAC counts); any nonzero diff
        // beyond display rounding means the export and the screen have diverged.
        const reconciliation = exportJobs.map((job) => {
            const jobRows = aggregated.filter(r => r.jobId === job.BU_ID);
            const excelCost = jobRows.filter(r => r.costType?.[0] === '5').reduce((s, r) => s + r.value, 0);
            const excelRevenue = -1 * jobRows.filter(r => r.costType?.[0] === '4').reduce((s, r) => s + r.value, 0);
            const excelOverhead = jobRows.filter(r => r.costType?.[0] === '6').reduce((s, r) => s + r.value, 0);
            const screenCost = pNum(EAC[job.BU_ID]) || 0;
            const screenRevenue = pNum(curBudget[job.BU_ID]) || 0;
            const screenOverhead = (isNaN(pNum(actualOverhead[job.BU_ID])) ? 0 : pNum(actualOverhead[job.BU_ID])) + (isNaN(pNum(etcOverhead[job.BU_ID])) ? 0 : pNum(etcOverhead[job.BU_ID]));
            return {
                jobId: job.BU_ID,
                excelCost: Math.round(excelCost * 100) / 100,
                screenCost: Math.round(screenCost * 100) / 100,
                costDiff: Math.round((excelCost - screenCost) * 100) / 100,
                excelRevenue: Math.round(excelRevenue * 100) / 100,
                screenRevenue: Math.round(screenRevenue * 100) / 100,
                revenueDiff: Math.round((excelRevenue - screenRevenue) * 100) / 100,
                excelOverhead: Math.round(excelOverhead * 100) / 100,
                screenOverhead: Math.round(screenOverhead * 100) / 100,
                overheadDiff: Math.round((excelOverhead - screenOverhead) * 100) / 100,
            };
        });

        const workbook = xlsx.utils.book_new();
        xlsx.utils.book_append_sheet(workbook, xlsx.utils.json_to_sheet(aggregated), "Job Cost Data");
        xlsx.utils.book_append_sheet(workbook, xlsx.utils.json_to_sheet(reconciliation), "Reconciliation");
        xlsx.writeFile(workbook, `ProjectionsExport${teamFileSuffix}.xlsx`);
    }

    useEffect(() => {
        if (!syncPeriod) setSyncPeriod(calculateDefaultPeriod());
    }, []);

    // ---- Build FusionEAC CSV + sync via Azure Function ----
    // includeTier stays true for every file that can reach JDE, the page's Download CSV included
    // (people drop that one in the input folder). The JDE orchestrator reads columns by position,
    // so without the tier column GLDate lands in Remarks and the batch never posts.
    // zeroLines (haZeroLines) clear HA accounts the file doesn't carry; baseRows is the
    // sync's line set after remapMissingBasePhase (defaults to the plain aggregation).
    const buildEACCSVContent = (includeTier = true, zeroLines = [], baseRows = null) => {
        const period = currentPeriod;
        // GL date = last day of the selected period month, M/D/YYYY like the files JDE has posted
        const glMonth = parseInt(period.periodNbr);
        const glYear = parseInt(period.key.split('-')[0]);
        const lastDay = new Date(glYear, glMonth, 0).getDate();
        const glDate = `${glMonth}/${lastDay}/${glYear}`;
        const fy = period.fiscalYearShort;

        // Aggregate by (jobId, costType, phaseCode) — JDE batch upload uses last
        // duplicate row, so combine them here. Sum value, keep first non-empty tier.
        const aggregated = [...(baseRows || aggregateEACRows(exportProjectionRows)), ...zeroLines];

        const sorted = aggregated.sort((a, b) => {
            if (a.jobId !== b.jobId) return a.jobId < b.jobId ? -1 : 1;
            if (a.costType !== b.costType) return a.costType < b.costType ? -1 : 1;
            return a.phaseCode < b.phaseCode ? -1 : a.phaseCode > b.phaseCode ? 1 : 0;
        });

        const header = includeTier
            ? 'jobId,costType,phaseCode,value,tier,Remarks,GLDate,FY'
            : 'jobId,costType,phaseCode,value,Remarks,GLDate,FY';
        const rows = sorted.map(row => {
            const remarks = 'Fusion EAC';
            const value = (Math.round(row.value * 100) / 100).toFixed(2);
            return includeTier
                ? `${row.jobId},${row.costType},${row.phaseCode},${value},${row.tier || '0'},${remarks},${glDate},${fy}`
                : `${row.jobId},${row.costType},${row.phaseCode},${value},${remarks},${glDate},${fy}`;
        });

        // CRLF for the JDE file, matching the uploads JDE has posted
        return includeTier
            ? [header, ...rows].join('\r\n') + '\r\n'
            : header + '\n' + rows.join('\n');
    };

    // HA balances by account for the jobs being synced -> 0 lines for accounts the file doesn't carry.
    // Also moves no-phase (100105) lines to 100101 where JDE has no 100105 account for the job.
    const loadEacZeroLines = async () => {
        setEacZeroLines(null);
        setEacRows(null);
        setEacZeroInfo({});
        try {
            const res = await loadJDEValues({ ids: exportJobs.map(j => j.BU_ID), wip: haWipDate, detail: true, jdeEnv: getReadEnv(jdeEnv) });
            const detail = res?.data;
            if (!Array.isArray(detail) || (detail.length && detail[0].Object_Account === undefined)) {
                throw new Error(res?.error || 'the API doesn\'t return HA account detail yet');
            }
            const base = aggregateEACRows(exportProjectionRows);
            const accounts = Array.isArray(res.accounts)
                ? new Set(res.accounts.map(a => `${String(a.BU_ID).trim()}|${String(a.Object_Account).trim()}|${String(a.Subsidiary).trim()}`))
                : null;
            const { rows, moved } = accounts ? remapMissingBasePhase(base, accounts) : { rows: base, moved: 0 };
            const tierByJob = Object.fromEntries(exportJobs.map(j => [j.BU_ID, j.Cat_Code_BU_16 || '']));
            setEacRows(rows);
            setEacZeroLines(haZeroLines(rows, detail, tierByJob));
            setEacZeroInfo({ source: res.source, liveError: res.liveError, moved, accountsChecked: !!accounts });
        } catch (err) {
            setEacZeroLines([]);
            setEacZeroInfo({ failed: err.message });
        }
    };

    const openSyncModal = () => {
        if (!exportProjectionRows.length) {
            alert('No projection rows to sync.');
            return;
        }
        setEacFolderError('');
        setShowSyncModal(true);
        loadEacZeroLines();
    };

    const chooseEacFolder = async () => {
        setEacFolderError('');
        try {
            setEacFolder(await pickFolder(jdeEnv));
        } catch (err) {
            if (err?.name !== 'AbortError') setEacFolderError(err.message);
        }
    };

    // Same file the sync writes, for when the folder can't be written from this browser
    const downloadJdeEacFile = () => {
        const blob = new Blob([buildEACCSVContent(true, eacZeroLines || [], eacRows)], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = EAC_FILE_NAME;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const [smb2Testing, setSmb2Testing] = useState(false);
    const [smb2Result, setSmb2Result] = useState(null);

    const testSmb2Write = async () => {
        setSmb2Testing(true);
        setSmb2Result(null);
        try {
            const csvContent = buildEACCSVContent();
            const apiUrl = process.env.REACT_APP_API_URL || 'https://forecastingprodservice.azurewebsites.net/';
            const response = await fetch(apiUrl + 'api/writeEACToShareSMB2', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ csvContent, fileName: 'FusionEAC.smb2test.csv' }),
            });
            const result = await response.json();
            setSmb2Result(result);
            if (result?.success) {
                alert(`SMB2 write OK — ${result.bytes} bytes to \\\\${result.host}\\${result.share}\\${result.path}`);
            } else {
                alert(`SMB2 write failed: ${result?.error || 'Unknown error'}${result?.code ? ` (${result.code})` : ''}`);
            }
        } catch (err) {
            setSmb2Result({ success: false, error: err.message });
            alert(`SMB2 request error: ${err.message}`);
        } finally {
            setSmb2Testing(false);
        }
    };

    // Moves no-phase (100105) lines to 100101 where JDE has no 100105 account for the job, like the
    // sync file; otherwise JDE books them on an account of its own choosing (10/30: revenue on 12426042)
    const downloadEACCsv = async () => {
        setEacCsvPreparing(true);
        let baseRows = null;
        try {
            const res = await loadJDEValues({ ids: exportJobs.map(j => j.BU_ID), wip: haWipDate, detail: true, jdeEnv: getReadEnv(jdeEnv) });
            if (!Array.isArray(res?.accounts)) throw new Error(res?.error || 'the API didn\'t return JDE accounts');
            const accounts = new Set(res.accounts.map(a => `${String(a.BU_ID).trim()}|${String(a.Object_Account).trim()}|${String(a.Subsidiary).trim()}`));
            baseRows = remapMissingBasePhase(aggregateEACRows(exportProjectionRows), accounts).rows;
        } catch (err) {
            if (!window.confirm(`Couldn't check JDE accounts (${err.message}), so no-phase lines stay on 100105 and JDE may post them to another account. Download anyway?`)) {
                return;
            }
        } finally {
            setEacCsvPreparing(false);
        }
        const csvContent = buildEACCSVContent(true, [], baseRows);
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `FusionEAC${teamFileSuffix}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const syncEACToJDE = async () => {
        // 1. Write FusionEAC.csv into the JDE input folder
        setEacFolderError('');
        if (!eacFolder) {
            setEacFolderError('Choose the JDE input folder first.');
            return;
        }
        const fileText = buildEACCSVContent(true, eacZeroLines || [], eacRows);
        const fileLines = fileText.trim().split('\r\n').length - 1;
        setEacWriting(true);
        try {
            if (!(await ensureWriteAccess(eacFolder))) {
                throw new Error('Write access to the folder wasn\'t allowed.');
            }
            const waiting = await existingFileSize(eacFolder, EAC_FILE_NAME);
            if (waiting > 0
                && !window.confirm(`${EAC_FILE_NAME} is already in the input folder (${waiting} bytes), so JDE hasn't picked up the last one. Replace it?`)) {
                return;
            }
            await writeTextFile(eacFolder, EAC_FILE_NAME, fileText, {
                onRetry: (err) => setEacFolderError(`${err.message} Retrying...`),
            });
            setEacFolderError('');
        } catch (err) {
            setEacFolderError(`Couldn't write ${EAC_FILE_NAME} (${err.message}). Nothing was sent to JDE. Download File gives you the same file to drop in the folder.`);
            return;
        } finally {
            setEacWriting(false);
        }

        // 2. Have the orchestrator pick it up
        await runEacOrchestrator(fileLines);
    };

    // The file was dropped in the input folder by hand (Download File), so only run the orchestrator
    const syncPlacedEacFile = async () => {
        if (!window.confirm(`Run the JDE sync on the ${EAC_FILE_NAME} already in ${inputFolderPath(jdeEnv)}?\n\nJDE (${jdeEnv}) loads whatever file is there, so make sure it's the one you just downloaded.`)) {
            return;
        }
        await runEacOrchestrator(null);
    };

    const runEacOrchestrator = async (fileLines) => {
        setShowSyncModal(false);

        setEacSyncing(true);
        setEacSyncResult(null);

        try {
            const response = await fetch(buildOrchUrl(jdeEnv, 'STR_ORCH_FusionEAC_BulkBatchUpload'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': getOrchAuth(jdeEnv)
                },
                body: JSON.stringify({
                    'User ID 1': '154045',
                    'Program ID 1': 'FusionEAC'
                })
            });
            const data = await response.json();
            setEacSyncing(false);
            if (data?.jde__status === 'SUCCESS') {
                setEacSyncResult({ success: true, rowCount: fileLines, orchResponse: data });
                alert(`EAC sync complete! ${fileLines !== null ? `${fileLines} lines written. ` : ''}Batch ${data['Batch Number'] || ''} (${data.jde__serverExecutionSeconds || '?'}s)`);
            } else {
                setEacSyncResult({ success: false, error: JSON.stringify(data?.message || data), orchResponse: data });
                alert(`EAC sync failed: ${JSON.stringify(data?.message || data)}`);
            }
        } catch (err) {
            setEacSyncing(false);
            setEacSyncResult({ success: false, error: err.message });
            alert(`EAC sync error: ${err.message}`);
        }
    };

    // ---- Sync Budget to JDE via Orchestrator (same API as budgets.js) ----
    const openBudgetSyncModal = () => {
        const candidates = selectedBudgetJobs.length > 0
            ? allJobs.filter(j => selectedBudgetJobs.includes(j.BU_ID) && jdeBudgetExists[j.BU_ID] === false)
            : allJobs.filter(j => jdeBudgetExists[j.BU_ID] === false);
        // Exclude any job that already has HA (actuals) in JDE — budget upload is blocked
        const blockedByHA = candidates.filter(j => jdeHAExists[j.BU_ID] === true);
        const jobsToSync = candidates.filter(j => jdeHAExists[j.BU_ID] !== true);
        if (jobsToSync.length === 0) {
            alert(
                candidates.length === 0
                    ? 'No jobs to sync — all selected jobs already have JA budgets in JDE.'
                    : `All ${candidates.length} eligible job(s) have HA (actuals) in JDE and cannot receive a budget upload.`
            );
            return;
        }
        setBudgetJobsToSync(jobsToSync);
        setBudgetJobsBlockedByHA(blockedByHA);
        setShowBudgetSyncModal(true);
    };

    const syncBudgetsToJDE = async () => {
        setShowBudgetSyncModal(false);
        const jobsToSync = budgetJobsToSync;
        if (!jobsToSync.length) return;
        const period = currentPeriod;

        setBudgetSyncing(true);
        setBudgetSyncResults(null);
        const failed = [];
        let postedCount = 0;

        for (let i = 0; i < jobsToSync.length; i++) {
            const job = jobsToSync[i];
            setBudgetSyncProgress({ current: i + 1, total: jobsToSync.length });

            try {
                const userData = await loadUserData({
                    id: job.BU_ID,
                    forecastGuid: selectedVersion,
                    live: false
                });
                const budgetData = userData?.data?.filter(d => d?.Forecast_Tab === 'Day Zero Budget');
                if (!budgetData || budgetData.length === 0) {
                    failed.push({ jobId: job.BU_ID, message: 'No budget data found' });
                    continue;
                }

                const aggregated = aggregateBudgetRows(budgetData);
                let rowFailed = false;

                for (let j = 0; j < aggregated.length; j++) {
                    const row = aggregated[j];
                    if (!row.amt && !row.units) {
                        continue;
                    }
                    if (j % 10 === 0 && j > 0) {
                        await new Promise(resolve => setTimeout(resolve, 1000));
                    }
                    try {
                        const response = await fetch(buildOrchUrl(jdeEnv, 'STR_ORCH_Fusion_UploadBudget'), {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': getOrchAuth(jdeEnv)
                            },
                            body: JSON.stringify(uploadBudgetPayload(job.BU_ID, row, period))
                        });
                        const data = await response.json();
                        if (data?.jde__status !== "SUCCESS") {
                            rowFailed = true;
                            failed.push({ jobId: job.BU_ID, costType: row.costType, costCode: row.costCode, message: JSON.stringify(data?.message) });
                        }
                    } catch (err) {
                        rowFailed = true;
                        failed.push({ jobId: job.BU_ID, costType: row.costType, costCode: row.costCode, message: err.message });
                    }
                    await new Promise(resolve => setTimeout(resolve, 50));
                }

                // Post the job once all its rows are staged. A job with a failed
                // row is left staged: posting it would give it a JA budget, and
                // this list only offers jobs without one, so the missing
                // accounts could not be synced again from here.
                if (rowFailed) {
                    failed.push({ jobId: job.BU_ID, message: 'Not posted because a row failed. Fix it, sync the job again, then tick it and Run Process Budget Upload.' });
                } else {
                    const result = await postJobBudget(job.BU_ID, period);
                    if (result.ok) {
                        postedCount++;
                    } else {
                        failed.push({ jobId: job.BU_ID, message: `Rows staged but not posted: ${result.error || JSON.stringify(result.data?.message || result.data)}` });
                    }
                }
                await new Promise(resolve => setTimeout(resolve, 200));
            } catch (error) {
                failed.push({ jobId: job.BU_ID, message: error.message });
            }
        }

        setBudgetSyncing(false);
        setBudgetSyncResults({ success: postedCount, failed: failed.length, failedDetails: failed });
        if (failed.length === 0) {
            alert(`Success! All ${postedCount} job(s) synced and posted in JDE.`);
        } else {
            alert(`Partial sync: ${postedCount} job(s) posted, ${failed.length} problem(s). See details below.`);
        }
    };

    // ---- Process Budget Upload (post step in JDE) ----
    // STR_ORCH_Fusion_ProcessBudgetUpload posts one job's staged F0902Z1 rows
    // (R14110 data selection MCU = inORCH_BusUnit) and recalculates its
    // projections, so it is called once per job.
    const postJobBudget = async (buId, period) => {
        try {
            const response = await fetch(buildOrchUrl(jdeEnv, 'STR_ORCH_Fusion_ProcessBudgetUpload'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': getOrchAuth(jdeEnv)
                },
                body: JSON.stringify(processBudgetPayload(buId, period))
            });
            const data = await response.json();
            return { ok: data?.jde__status === 'SUCCESS', status: response.status, data };
        } catch (err) {
            return { ok: false, error: err.message };
        }
    };

    // The manual button posts the ticked jobs: a retry for a job whose rows
    // were staged but not posted. It is gated behind a confirmation modal so
    // the operator can verify env / period before posting.
    const runProcessBudgetUpload = async () => {
        setShowProcessBudgetModal(false);
        const period = currentPeriod;
        const jobIds = selectedBudgetJobs;
        if (!jobIds.length) return;
        setProcessBudgetRunning(true);
        setProcessBudgetResult(null);
        try {
            const results = [];
            for (const jobId of jobIds) {
                results.push({ jobId, ...(await postJobBudget(jobId, period)) });
            }
            const ok = results.every(r => r.ok);
            setProcessBudgetResult({ ok, results });
            if (ok) {
                alert(`Process Budget Upload succeeded for ${results.length} job(s), P${period.periodNbr} FY${period.fiscalYearShort}.`);
            } else {
                alert(`Process Budget Upload: ${results.filter(r => !r.ok).length} of ${results.length} job(s) returned non-success. See details below.`);
            }
        } catch (err) {
            setProcessBudgetResult({ ok: false, error: err.message });
            alert(`Process Budget Upload failed: ${err.message}`);
        } finally {
            setProcessBudgetRunning(false);
        }
    };

    // ---- Revised Budget (Change Orders) ----
    const REVENUE_FIELDS = ['tAndM', 'changeOrders', 'unitPrice'];
    const REDUCTION_FIELDS = ['reductionOfScope'];

    const aggregateCoRecords = (records, jobsById) => {
        const byRowId = {};
        for (const r of records) {
            const rowId = r.Forecast_Row_ID;
            if (!rowId) continue;
            if (!byRowId[rowId]) {
                byRowId[rowId] = {
                    rowId,
                    buId: r.BU_ID,
                    jobName: jobsById[r.BU_ID]?.BU_Description || '',
                    coNo: r.Row_Label || '',
                    description: '',
                    status: r.Status || '',
                    costCode: r.Cost_Code || '',
                    resourceCode: r.Resource_Code || '',
                    revenue: 0,
                    reduction: 0,
                    cost: 0,
                    hours: 0,
                    qty: 0,
                    byCostType: {},
                    records: []
                };
            }
            const co = byRowId[rowId];
            co.records.push(r);
            if (!co.description && r.Description) co.description = r.Description;
            if (!co.status && r.Status) co.status = r.Status;
            if (!co.costCode && r.Cost_Code) co.costCode = r.Cost_Code;
            if (!co.resourceCode && r.Resource_Code) co.resourceCode = r.Resource_Code;
            if (!co.coNo && r.Row_Label) co.coNo = r.Row_Label;

            const fieldName = r.Forecast_Field_Name || '';
            const val = pNum(r.Value_Amount);
            if (REVENUE_FIELDS.includes(fieldName)) {
                co.revenue += val;
            } else if (REDUCTION_FIELDS.includes(fieldName)) {
                co.reduction += val;
            } else if (fieldName.length === 5 && fieldName.startsWith('5')) {
                co.cost += val;
                co.byCostType[fieldName] = (co.byCostType[fieldName] || 0) + val;
            } else if (fieldName === 'totalLaborHours' || fieldName === 'totalEquipmentHours' || fieldName === 'Hours') {
                co.hours += val;
            } else if (fieldName === 'budgetQty' || fieldName === 'Quantity') {
                co.qty += val;
            }
        }
        return Object.values(byRowId).filter(co => co.coNo || co.revenue || co.cost);
    };

    useEffect(() => {
        if (activeTab !== 'Revised Budget') return;
        if (!filteredJobs.length || !selectedVersion) return;
        let cancelled = false;
        (async () => {
            setCoLoading(true);
            try {
                const ids = filteredJobs.map(j => j.BU_ID);
                const result = await loadTeamUserDataMain({ ids, forecastGuid: selectedVersion });
                if (cancelled) return;
                const coRecords = (result?.data || []).filter(d =>
                    d?.Forecast_Tab === 'CO Log' || d?.Forecast_Tab === 'CO LOG'
                );
                const jobsById = Object.fromEntries(filteredJobs.map(j => [j.BU_ID, j]));
                const aggregated = aggregateCoRecords(coRecords, jobsById);
                aggregated.sort((a, b) => {
                    if (a.buId !== b.buId) return String(a.buId).localeCompare(String(b.buId));
                    return (parseFloat(a.coNo) || 0) - (parseFloat(b.coNo) || 0);
                });
                setCoRawRecords(coRecords);
                setCoRows(aggregated);
            } catch (err) {
                console.error('Error loading CO Log data:', err);
            } finally {
                if (!cancelled) setCoLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [activeTab, filteredJobs, selectedVersion]);

    const filteredCoRows = coStatusFilter === 'All' ? coRows : coRows.filter(r => (r.status || '').toLowerCase() === coStatusFilter.toLowerCase());

    const coStatusOptions = (() => {
        const s = new Set();
        coRows.forEach(r => { if (r.status) s.add(r.status); });
        return ['All', ...Array.from(s).sort()];
    })();

    const downloadCoCsv = () => {
        const headers = ['Job', 'Job Name', 'CO No', 'Description', 'Status', 'Cost Code', 'Resource', 'Revenue', 'Reduction', 'Cost', 'Net Revenue', 'GP', 'GP%', 'Hours', 'Qty'];
        const rows = filteredCoRows.map(co => {
            const netRev = co.revenue - co.reduction;
            const gp = netRev - co.cost;
            const gpPct = netRev ? (gp / netRev) * 100 : 0;
            const esc = v => {
                const s = v == null ? '' : String(v);
                return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
            };
            return [co.buId, co.jobName, co.coNo, co.description, co.status, co.costCode, co.resourceCode,
                co.revenue.toFixed(2), co.reduction.toFixed(2), co.cost.toFixed(2),
                netRev.toFixed(2), gp.toFixed(2), gpPct.toFixed(1),
                co.hours.toFixed(2), co.qty.toFixed(2)].map(esc).join(',');
        });
        const csvContent = [headers.join(','), ...rows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `ChangeOrders_${moment().format('YYYYMMDD_HHmmss')}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const syncCoToJDE = async () => {
        setShowCoSyncModal(false);
        if (!coRows.length) return;
        const period = currentPeriod;

        setCoSyncing(true);
        setCoSyncResults(null);
        const failed = [];
        let successCount = 0;
        const syncable = filteredCoRows;

        for (let i = 0; i < syncable.length; i++) {
            const co = syncable[i];
            setCoSyncProgress({ current: i + 1, total: syncable.length });

            const payloads = [];
            const netRevenue = co.revenue - co.reduction;
            if (netRevenue) {
                payloads.push({
                    inORCH_BusUnit: co.buId,
                    inORCH_Object: '41000',
                    inORCH_Sub: co.costCode || '',
                    inORCH_UserID: '154045',
                    inORCH_BudgetAmt: -1 * netRevenue,
                    inORCH_Cent: period.century,
                    inORCH_FY: period.fiscalYearShort,
                    inORCH_BegPeriodNbr: period.periodNbr,
                    inORCH_EndPeriodNbr: period.periodNbr,
                    inORCH_BegFiscalYear: period.fiscalYear,
                    inORCH_EndFiscalYear: period.fiscalYear
                });
            }
            for (const [costType, amt] of Object.entries(co.byCostType)) {
                if (!amt) continue;
                payloads.push({
                    inORCH_BusUnit: co.buId,
                    inORCH_Object: costType,
                    inORCH_Sub: co.costCode || '',
                    inORCH_UserID: '154045',
                    inORCH_BudgetAmt: amt,
                    inORCH_Cent: period.century,
                    inORCH_FY: period.fiscalYearShort,
                    inORCH_BegPeriodNbr: period.periodNbr,
                    inORCH_EndPeriodNbr: period.periodNbr,
                    inORCH_BegFiscalYear: period.fiscalYear,
                    inORCH_EndFiscalYear: period.fiscalYear
                });
            }

            for (let j = 0; j < payloads.length; j++) {
                const payload = payloads[j];
                if (j % 10 === 0 && j > 0) {
                    await new Promise(resolve => setTimeout(resolve, 1000));
                }
                try {
                    const response = await fetch(buildOrchUrl(jdeEnv, 'STR_ORCH_Fusion_UploadBudget'), {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': getOrchAuth(jdeEnv)
                        },
                        body: JSON.stringify(payload)
                    });
                    const data = await response.json();
                    if (data?.jde__status !== "SUCCESS") {
                        failed.push({ jobId: co.buId, coNo: co.coNo, costType: payload.inORCH_Object, costCode: payload.inORCH_Sub, message: JSON.stringify(data?.message) });
                    }
                } catch (err) {
                    failed.push({ jobId: co.buId, coNo: co.coNo, costType: payload.inORCH_Object, costCode: payload.inORCH_Sub, message: err.message });
                }
                await new Promise(resolve => setTimeout(resolve, 50));
            }
            successCount++;
        }

        // try {
        //     const finishResponse = await fetch(buildOrchUrl(jdeEnv, 'STR_ORCH_Fusion_ProcessBudgetUpload'), {
        //         method: 'POST',
        //         headers: {
        //             'Content-Type': 'application/json',
        //             'Authorization': 'Basic MTQ4Njk1Oks2b1chKmVJZ216dDZWd2Y='
        //         },
        //         body: JSON.stringify({
        //             "inORCH_Cent": period.century,
        //             "inORCH_FY": period.fiscalYearShort,
        //             "inORCH_BegPeriodNbr": period.periodNbr,
        //             "inORCH_EndPeriodNbr": period.periodNbr,
        //             "inORCH_BegFiscalYear": period.fiscalYear,
        //             "inORCH_EndFiscalYear": period.fiscalYear
        //         })
        //     });
        //     const finishData = await finishResponse.json();
        //     if (finishData?.jde__status !== "SUCCESS") {
        //         console.error('Process Budget Upload Error:', finishData);
        //     }
        // } catch (error) {
        //     console.error('Process Budget Upload Error:', error);
        // }

        setCoSyncing(false);
        setCoSyncResults({ success: successCount, failed: failed.length, failedDetails: failed });
        if (failed.length === 0) {
            alert(`Success! ${successCount} change order(s) synced to JDE.`);
        } else {
            alert(`Partial sync: ${successCount} COs processed, ${failed.length} line(s) failed. See details below.`);
        }
    };

    return (
        <View style={{ flex: 1, backgroundColor: '#fff' }}>
            {/* Hidden Summary component — source of truth for summary validation */}
            <View style={{ display: 'none' }}>
                <Summary forceLoadAll={true} onSummaryData={setSummaryData} onUserData={setRawUserData} onCostDetailData={setRawCostDetailData} onCommitmentsData={setRawCommitmentsData} onBillingsData={setRawBillingsData} />
            </View>
            {/* Tab Navigation */}
            <View style={{
                backgroundColor: '#ffffff',
                borderBottom: '1px solid #e2e8f0',
                paddingLeft: 20,
                paddingTop: 8
            }}>
                <View style={{ flexDirection: 'row', gap: 20, marginLeft: 10, height: 42, alignItems: 'center' }}>
                    {['Projections', 'Budget', 'Revised Budget'].map((tab) => (
                        <Pressable
                            key={tab}
                            onPress={() => setActiveTab(tab)}
                            style={{
                                paddingVertical: 12,
                                paddingHorizontal: 16,
                                borderBottom: activeTab === tab ? '2px solid #2563eb' : '2px solid transparent',
                                backgroundColor: 'transparent',
                                justifyContent: 'center',
                            }}
                        >
                            <Text style={{
                                fontSize: 14,
                                color: activeTab === tab ? '#111827' : '#6b7280',
                                fontWeight: activeTab === tab ? '600' : '400',
                            }}>
                                {tab}
                            </Text>
                        </Pressable>
                    ))}
                    <View style={{ flexDirection: 'row', marginLeft: 'auto', marginRight: 20, alignItems: 'center', gap: 10 }}>
                        <Text style={{ fontSize: 12, color: '#6b7280', fontWeight: '600' }}>JDE Env:</Text>
                        <View style={{ flexDirection: 'row', borderRadius: 6, border: '1px solid #d1d5db', overflow: 'hidden', height: 30 }}>
                            {[JDE_ENV_PROD, JDE_ENV_TEST, JDE_ENV_DEV].map((env, i) => (
                                <Pressable
                                    key={env}
                                    onPress={() => setJdeEnv(env)}
                                    style={{
                                        padding: '6px 16px',
                                        minWidth: 60,
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        backgroundColor: jdeEnv === env ? envColor(env) : '#f9fafb',
                                        borderLeft: i > 0 ? '1px solid #d1d5db' : 'none',
                                    }}
                                >
                                    <Text style={{
                                        fontSize: 12,
                                        lineHeight: 16,
                                        color: jdeEnv === env ? '#fff' : '#6b7280',
                                        fontWeight: '700',
                                        letterSpacing: 0.5,
                                        textTransform: 'uppercase',
                                    }}>
                                        {env}
                                    </Text>
                                </Pressable>
                            ))}
                        </View>
                    </View>
                </View>
            </View>

            {/* Tab Content */}
            <View style={{ flex: 1, backgroundColor: '#fff' }}>
                {activeTab === 'Projections' ? (
                    <View style={{ padding: 20 }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 16, gap: 12 }}>
                            <Text style={{ fontSize: 13, color: '#0369a1', fontWeight: '600' }}>JDE Period:</Text>
                            <select
                                value={currentPeriod.key}
                                onChange={(e) => {
                                    const opt = getPeriodOptions().find(p => p.key === e.target.value);
                                    if (opt) setSyncPeriod(opt);
                                }}
                                style={{ fontSize: 13, padding: '4px 8px', borderRadius: 4, border: '1px solid #7dd3fc', backgroundColor: '#fff', color: '#0c4a6e', fontWeight: '600' }}
                            >
                                {getPeriodOptions().map(p => (
                                    <option key={p.key} value={p.key}>{p.displayDate} (P{p.periodNbr} FY{p.fiscalYearShort})</option>
                                ))}
                            </select>
                            <Text style={{ fontSize: 13, color: '#0369a1', fontWeight: '600', marginLeft: 8 }}>Team:</Text>
                            <select
                                value={teamFilter}
                                onChange={(e) => setTeamFilter(e.target.value)}
                                style={{ fontSize: 13, padding: '4px 8px', borderRadius: 4, border: '1px solid #7dd3fc', backgroundColor: teamFilter === 'All' ? '#fff' : '#e0f2fe', color: '#0c4a6e', fontWeight: '600' }}
                            >
                                <option value="All">All teams ({filteredJobs.length})</option>
                                {teamOptions.map(t => (
                                    <option key={t.code} value={t.code}>{t.label} ({t.count})</option>
                                ))}
                            </select>
                            {!(haDataLoaded && projectionDataLoaded) && filteredJobs.length > 0 && (
                                <Text style={{ fontSize: 12, color: '#f59e0b', fontWeight: '500' }}>Loading...</Text>
                            )}
                            <Pressable
                                style={{ backgroundColor: (haDataLoaded && projectionDataLoaded) ? '#e0e0e0' : '#f3f4f6', padding: 10, borderRadius: 5, marginLeft: 'auto', opacity: (haDataLoaded && projectionDataLoaded) ? 1 : 0.5 }}
                                onPress={exportCurrentValues}
                                disabled={!(haDataLoaded && projectionDataLoaded)}
                            >
                                <Text style={{ color: (haDataLoaded && projectionDataLoaded) ? '#000' : '#9ca3af' }}>{(haDataLoaded && projectionDataLoaded) ? 'Export Projections Excel' : 'Loading data...'}</Text>
                            </Pressable>
                            <Pressable
                                style={{ backgroundColor: (haDataLoaded && projectionDataLoaded && !eacCsvPreparing) ? '#0ea5e9' : '#bae6fd', padding: 10, borderRadius: 5, opacity: (haDataLoaded && projectionDataLoaded && !eacCsvPreparing) ? 1 : 0.5 }}
                                onPress={downloadEACCsv}
                                disabled={!(haDataLoaded && projectionDataLoaded) || eacCsvPreparing}
                            >
                                <Text style={{ color: '#fff', fontWeight: '600', fontSize: 13 }}>{eacCsvPreparing ? 'Checking JDE accounts...' : 'Download CSV'}</Text>
                            </Pressable>
                            <Pressable
                                style={{ backgroundColor: (haDataLoaded && projectionDataLoaded && !smb2Testing) ? '#7c3aed' : '#c4b5fd', padding: 10, borderRadius: 5, opacity: (haDataLoaded && projectionDataLoaded && !smb2Testing) ? 1 : 0.5 }}
                                onPress={testSmb2Write}
                                disabled={!(haDataLoaded && projectionDataLoaded) || smb2Testing}
                            >
                                <Text style={{ color: '#fff', fontWeight: '600', fontSize: 13 }}>{smb2Testing ? 'Testing SMB2...' : 'Test SMB2 Write'}</Text>
                            </Pressable>
                            {!eacSyncing ? (
                                <Pressable
                                    style={{ backgroundColor: (haDataLoaded && projectionDataLoaded) ? '#2563eb' : '#93c5fd', padding: 10, borderRadius: 5, opacity: (haDataLoaded && projectionDataLoaded) ? 1 : 0.5 }}
                                    onPress={openSyncModal}
                                    disabled={!(haDataLoaded && projectionDataLoaded)}
                                >
                                    <Text style={{ color: '#fff', fontWeight: '600', fontSize: 13 }}>Sync EAC to JDE</Text>
                                </Pressable>
                            ) : (
                                <View style={{ backgroundColor: '#dbeafe', padding: 10, borderRadius: 5 }}>
                                    <Text style={{ color: '#1e40af', fontSize: 13, fontWeight: '500' }}>Syncing EAC...</Text>
                                </View>
                            )}
                            <Text style={{ color: '#6b7280', fontSize: 13 }}>
                                {teamFilter === 'All' ? filteredJobs.length : `${visibleJobs.length} of ${filteredJobs.length}`} jobs &middot; {exportProjectionRows.length} projection rows
                                {!(haDataLoaded && projectionDataLoaded) ? ' \u00b7 Comparing...' : ''}
                            </Text>
                            {tickedCount > 0 && (
                                <Text style={{ color: '#1d4ed8', fontSize: 13, fontWeight: '600' }}>{tickedCount} selected</Text>
                            )}
                            <Pressable
                                style={{ padding: '4px 8px', borderRadius: 4, borderWidth: 1, borderColor: '#fca5a5', opacity: (haDataLoaded && projectionDataLoaded) ? 1 : 0.5 }}
                                onPress={selectOutOfSyncJobs}
                                disabled={!(haDataLoaded && projectionDataLoaded)}
                            >
                                <Text style={{ fontSize: 12, color: '#b91c1c', fontWeight: '600' }}>Select {'\u2717'} HA</Text>
                            </Pressable>
                            {tickedCount > 0 && (
                                <Pressable style={{ padding: '4px 8px' }} onPress={() => setSyncSelection(new Set())}>
                                    <Text style={{ fontSize: 12, color: '#475569', textDecorationLine: 'underline' }}>Clear</Text>
                                </Pressable>
                            )}
                            {eacSyncResult && (
                                <Text style={{ fontSize: 12, color: eacSyncResult.success ? '#10b981' : '#ef4444', fontWeight: '500' }}>
                                    {eacSyncResult.success ? `\u2713 Last sync: ${eacSyncResult.rowCount !== null ? `${eacSyncResult.rowCount} rows` : 'file in folder'}` : `\u2717 ${eacSyncResult.error}`}
                                </Text>
                            )}
                        </View>

                        {/* Job Summary Table */}
                        <View style={{ borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
                            {/* Group Header Row */}
                            <View style={{ flexDirection: 'row', backgroundColor: '#e2e8f0', borderBottomWidth: 1, borderColor: '#cbd5e1' }}>
                                <Text style={{ width: 338, padding: 4, fontSize: 11, fontWeight: '700', color: '#64748b' }}></Text>
                                <View style={{ width: 355, borderLeftWidth: 2, borderColor: '#2563eb', backgroundColor: '#eff6ff' }}>
                                    <Text style={{ padding: 4, fontSize: 11, fontWeight: '700', color: '#1e40af', textAlign: 'center' }}>FUSION PROJECTION</Text>
                                </View>
                                <View style={{ width: 190, borderLeftWidth: 2, borderColor: '#1e40af', backgroundColor: '#dbeafe' }}>
                                    <Text style={{ padding: 4, fontSize: 11, fontWeight: '700', color: '#1e3a8a', textAlign: 'center' }}>FUSION SUMMARY</Text>
                                </View>
                                <View style={{ width: 265, borderLeftWidth: 2, borderColor: '#1d4ed8', backgroundColor: '#e0e7ff' }}>
                                    <Text style={{ padding: 4, fontSize: 11, fontWeight: '700', color: '#312e81', textAlign: 'center' }}>JDE HA</Text>
                                </View>
                                <View style={{ width: 180, borderLeftWidth: 2, borderColor: '#7c3aed', backgroundColor: '#f5f3ff' }}>
                                    <Text style={{ padding: 4, fontSize: 11, fontWeight: '700', color: '#5b21b6', textAlign: 'center' }}>SUMMARY VAR</Text>
                                </View>
                                <View style={{ width: 250, borderLeftWidth: 2, borderColor: '#4f46e5', backgroundColor: '#eef2ff' }}>
                                    <Text style={{ padding: 4, fontSize: 11, fontWeight: '700', color: '#3730a3', textAlign: 'center' }}>HA VARIANCE</Text>
                                </View>
                            </View>
                            {/* Column Header Row */}
                            <View style={{ flexDirection: 'row', backgroundColor: '#f1f5f9', borderBottomWidth: 1, borderColor: '#e2e8f0' }}>
                                <View style={{ width: 28, padding: 6, justifyContent: 'center' }}>
                                    <input type="checkbox" title="Select all shown" checked={allVisibleTicked} onChange={toggleAllVisibleJobs} style={{ margin: 0, cursor: 'pointer' }} />
                                </View>
                                <Text style={{ width: 60, padding: 6, fontSize: 11, fontWeight: '600', color: '#374151' }}>Status</Text>
                                <Text style={{ width: 80, padding: 6, fontSize: 11, fontWeight: '600', color: '#374151' }}>Job #</Text>
                                <Text style={{ width: 40, padding: 6, fontSize: 11, fontWeight: '600', color: '#374151' }}>Type</Text>
                                <Text style={{ width: 130, padding: 6, fontSize: 11, fontWeight: '600', color: '#374151' }}>Job Name</Text>
                                {/* Projection columns */}
                                <Text style={{ width: 95, padding: 6, fontSize: 11, fontWeight: '600', color: '#1e40af', textAlign: 'right', borderLeftWidth: 2, borderColor: '#2563eb' }}>Revenue</Text>
                                <Text style={{ width: 95, padding: 6, fontSize: 11, fontWeight: '600', color: '#1e40af', textAlign: 'right' }}>Cost</Text>
                                <Text style={{ width: 75, padding: 6, fontSize: 11, fontWeight: '600', color: '#1e40af', textAlign: 'right' }}>OH 60900</Text>
                                <Text style={{ width: 45, padding: 6, fontSize: 11, fontWeight: '600', color: '#1e40af', textAlign: 'right' }}>JP%</Text>
                                <Text style={{ width: 45, padding: 6, fontSize: 11, fontWeight: '600', color: '#1e40af', textAlign: 'right' }}>GM%</Text>
                                {/* Summary columns */}
                                <Text style={{ width: 95, padding: 6, fontSize: 11, fontWeight: '600', color: '#1e3a8a', textAlign: 'right', borderLeftWidth: 2, borderColor: '#1e40af' }}>Revenue</Text>
                                <Text style={{ width: 95, padding: 6, fontSize: 11, fontWeight: '600', color: '#1e3a8a', textAlign: 'right' }}>Cost</Text>
                                {/* HA columns */}
                                <Text style={{ width: 95, padding: 6, fontSize: 11, fontWeight: '600', color: '#312e81', textAlign: 'right', borderLeftWidth: 2, borderColor: '#1d4ed8' }}>Revenue</Text>
                                <Text style={{ width: 95, padding: 6, fontSize: 11, fontWeight: '600', color: '#312e81', textAlign: 'right' }}>Cost</Text>
                                <Text style={{ width: 75, padding: 6, fontSize: 11, fontWeight: '600', color: '#312e81', textAlign: 'right' }}>OH 60900</Text>
                                {/* Summary Variance columns */}
                                <Text style={{ width: 90, padding: 6, fontSize: 11, fontWeight: '600', color: '#5b21b6', textAlign: 'right', borderLeftWidth: 2, borderColor: '#7c3aed' }}>Rev Diff</Text>
                                <Text style={{ width: 90, padding: 6, fontSize: 11, fontWeight: '600', color: '#5b21b6', textAlign: 'right' }}>Cost Diff</Text>
                                {/* HA Variance columns */}
                                <Text style={{ width: 90, padding: 6, fontSize: 11, fontWeight: '600', color: '#3730a3', textAlign: 'right', borderLeftWidth: 2, borderColor: '#4f46e5' }}>Rev Diff</Text>
                                <Text style={{ width: 90, padding: 6, fontSize: 11, fontWeight: '600', color: '#3730a3', textAlign: 'right' }}>Cost Diff</Text>
                                <Text style={{ width: 70, padding: 6, fontSize: 11, fontWeight: '600', color: '#3730a3', textAlign: 'right' }}>OH Diff</Text>
                            </View>
                            {/* Rows */}
                            {(sortedFilteredJobs.length ? sortedFilteredJobs : filteredJobs).filter(job => teamFilter === 'All' || job.Cat_Code_BU_27 === teamFilter).map((job, i) => {
                                const comp = jobComparison[job.BU_ID];
                                const dataReady = haDataLoaded && projectionDataLoaded;
                                const statusColor = !dataReady ? '#9ca3af' : comp?.inSync ? '#10b981' : comp?.source === 'HA' ? '#ef4444' : comp?.source === 'Summary' ? '#f59e0b' : '#9ca3af';
                                const statusText = !dataReady ? '...' : comp?.inSync ? '\u2713 Sync' : comp?.source === 'HA' ? '\u2717 HA' : comp?.source === 'Summary' ? '~ Sum' : '\u2014';
                                const rowBg = !dataReady ? (i % 2 === 0 ? '#fff' : '#fafbfc') : !comp?.inSync && comp?.source ? (comp.source === 'HA' ? '#fef2f2' : '#fffbeb') : (i % 2 === 0 ? '#fff' : '#fafbfc');
                                return (
                                    <View key={job.BU_ID} style={{ flexDirection: 'row', borderBottomWidth: 1, borderColor: '#f1f5f9', backgroundColor: syncSelection.has(job.BU_ID) ? '#dbeafe' : rowBg }}>
                                        <View style={{ width: 28, padding: 6, justifyContent: 'center' }}>
                                            <input type="checkbox" checked={syncSelection.has(job.BU_ID)} onChange={() => toggleSyncJob(job.BU_ID)} style={{ margin: 0, cursor: 'pointer' }} />
                                        </View>
                                        <Text style={{ width: 60, padding: 6, fontSize: 11, color: statusColor, fontWeight: '600' }}>{statusText}</Text>
                                        <Text style={{ width: 80, padding: 6, fontSize: 11, color: '#374151' }}>{job.BU_ID}</Text>
                                        <Text style={{ width: 40, padding: 6, fontSize: 11, color: '#6b7280' }}>{job.Cat_Code_BU_08 == 'TMN' ? 'NTE' : job.Cat_Code_BU_08}</Text>
                                        <Text style={{ width: 130, padding: 6, fontSize: 11, color: '#374151', textWrap: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} numberOfLines={1}>{job.BU_Description}</Text>
                                        {/* Projection values */}
                                        <Text style={{ width: 95, padding: 6, fontSize: 11, color: '#1e3a5f', textAlign: 'right', borderLeftWidth: 2, borderColor: '#2563eb', backgroundColor: i % 2 === 0 ? '#f0f7ff' : '#e8f2ff' }}>{curBudget[job.BU_ID] ? fNum(pNum(curBudget[job.BU_ID]), 1) : ''}</Text>
                                        <Text style={{ width: 95, padding: 6, fontSize: 11, color: '#1e3a5f', textAlign: 'right', backgroundColor: i % 2 === 0 ? '#f0f7ff' : '#e8f2ff' }}>{EAC[job.BU_ID] ? fNum(pNum(EAC[job.BU_ID]), 1) : ''}</Text>
                                        <Text style={{ width: 75, padding: 6, fontSize: 11, color: '#1e3a5f', textAlign: 'right', backgroundColor: i % 2 === 0 ? '#f0f7ff' : '#e8f2ff' }}>{comp?.ourOverhead ? fNum(comp.ourOverhead, 1) : ''}</Text>
                                        <Text style={{ width: 45, padding: 6, fontSize: 11, color: '#1e3a5f', textAlign: 'right', backgroundColor: i % 2 === 0 ? '#f0f7ff' : '#e8f2ff' }}>{jobProfitPerc[job.BU_ID] ? fNum(pNum(jobProfitPerc[job.BU_ID]), 1) + '%' : ''}</Text>
                                        <Text style={{ width: 45, padding: 6, fontSize: 11, color: '#1e3a5f', textAlign: 'right', backgroundColor: i % 2 === 0 ? '#f0f7ff' : '#e8f2ff' }}>{grossMarginPerc[job.BU_ID] ? fNum(pNum(grossMarginPerc[job.BU_ID]), 1) + '%' : ''}</Text>
                                        {/* Summary values */}
                                        <Text style={{ width: 95, padding: 6, fontSize: 11, color: '#1e3a8a', textAlign: 'right', borderLeftWidth: 2, borderColor: '#1e40af', backgroundColor: i % 2 === 0 ? '#dbeafe' : '#bfdbfe' }}>{dataReady && comp?.summaryRevenue ? fNum(comp.summaryRevenue, 1) : ''}</Text>
                                        <Text style={{ width: 95, padding: 6, fontSize: 11, color: '#1e3a8a', textAlign: 'right', backgroundColor: i % 2 === 0 ? '#dbeafe' : '#bfdbfe' }}>{dataReady && comp?.summaryCost ? fNum(comp.summaryCost, 1) : ''}</Text>
                                        {/* HA values */}
                                        <Text style={{ width: 95, padding: 6, fontSize: 11, color: '#312e81', textAlign: 'right', borderLeftWidth: 2, borderColor: '#1d4ed8', backgroundColor: i % 2 === 0 ? '#e0e7ff' : '#c7d2fe' }}>{dataReady && comp?.compareRevenue ? fNum(comp.compareRevenue, 1) : ''}</Text>
                                        <Text style={{ width: 95, padding: 6, fontSize: 11, color: '#312e81', textAlign: 'right', backgroundColor: i % 2 === 0 ? '#e0e7ff' : '#c7d2fe' }}>{dataReady && comp?.compareCost ? fNum(comp.compareCost, 1) : ''}</Text>
                                        <Text style={{ width: 75, padding: 6, fontSize: 11, color: '#312e81', textAlign: 'right', backgroundColor: i % 2 === 0 ? '#e0e7ff' : '#c7d2fe' }}>{dataReady && comp?.compareOverhead ? fNum(comp.compareOverhead, 1) : ''}</Text>
                                        {/* Summary Variance */}
                                        <Text style={{ width: 90, padding: 6, fontSize: 11, color: dataReady && comp?.summaryRevDiff > 1 ? '#dc2626' : '#5b21b6', textAlign: 'right', fontWeight: dataReady && comp?.summaryRevDiff > 1 ? '600' : '400', borderLeftWidth: 2, borderColor: '#7c3aed' }}>{dataReady && comp?.summaryRevDiff > 0 ? fNum(comp.summaryRevDiff, 1) : ''}</Text>
                                        <Text style={{ width: 90, padding: 6, fontSize: 11, color: dataReady && comp?.summaryCostDiff > 1 ? '#dc2626' : '#5b21b6', textAlign: 'right', fontWeight: dataReady && comp?.summaryCostDiff > 1 ? '600' : '400' }}>{dataReady && comp?.summaryCostDiff > 0 ? fNum(comp.summaryCostDiff, 1) : ''}</Text>
                                        {/* HA Variance */}
                                        <Text style={{ width: 90, padding: 6, fontSize: 11, color: dataReady && comp?.revenueDiff > 1 ? '#dc2626' : '#3730a3', textAlign: 'right', fontWeight: dataReady && comp?.revenueDiff > 1 ? '600' : '400', borderLeftWidth: 2, borderColor: '#4f46e5' }}>{dataReady && comp?.revenueDiff > 0 ? fNum(comp.revenueDiff, 1) : ''}</Text>
                                        <Text style={{ width: 90, padding: 6, fontSize: 11, color: dataReady && comp?.costDiff > 1 ? '#dc2626' : '#3730a3', textAlign: 'right', fontWeight: dataReady && comp?.costDiff > 1 ? '600' : '400' }}>{dataReady && comp?.costDiff > 0 ? fNum(comp.costDiff, 1) : ''}</Text>
                                        <Text style={{ width: 70, padding: 6, fontSize: 11, color: dataReady && comp?.overheadDiff > 1 ? '#dc2626' : '#3730a3', textAlign: 'right', fontWeight: dataReady && comp?.overheadDiff > 1 ? '600' : '400' }}>{dataReady && comp?.overheadDiff > 0 ? fNum(comp.overheadDiff, 1) : ''}</Text>
                                    </View>
                                );
                            })}
                        </View>
                    </View>
                ) : null}

                {activeTab === 'Budget' ? (
                    <View style={{ padding: 20 }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                            <Text style={{ fontSize: 16, fontWeight: '600', color: '#111827' }}>
                                Jobs Missing JA Budget in JDE
                            </Text>
                            <View style={{ flexDirection: 'row', gap: 8 }}>
                                {!budgetSyncing ? (
                                    <Pressable
                                        onPress={openBudgetSyncModal}
                                        style={{ backgroundColor: '#2563eb', padding: '10px 18px', borderRadius: 6 }}
                                    >
                                        <Text style={{ color: '#fff', fontSize: 13, fontWeight: '600' }}>
                                            {selectedBudgetJobs.length > 0 ? `Sync ${selectedBudgetJobs.length} Selected to JDE` : 'Sync All Missing to JDE'}
                                        </Text>
                                    </Pressable>
                                ) : (
                                    <View style={{ backgroundColor: '#dbeafe', padding: '10px 18px', borderRadius: 6 }}>
                                        <Text style={{ color: '#1e40af', fontSize: 13, fontWeight: '500' }}>
                                            Syncing... {budgetSyncProgress.current}/{budgetSyncProgress.total}
                                        </Text>
                                    </View>
                                )}
                                {!processBudgetRunning ? (
                                    <Pressable
                                        onPress={() => { setProcessBudgetResult(null); setShowProcessBudgetModal(true); }}
                                        style={{ backgroundColor: '#7c3aed', padding: '10px 18px', borderRadius: 6 }}
                                        title="Run STR_ORCH_Fusion_ProcessBudgetUpload — posts the staged budget rows for the ticked jobs"
                                    >
                                        <Text style={{ color: '#fff', fontSize: 13, fontWeight: '600' }}>
                                            Run Process Budget Upload
                                        </Text>
                                    </Pressable>
                                ) : (
                                    <View style={{ backgroundColor: '#ede9fe', padding: '10px 18px', borderRadius: 6 }}>
                                        <Text style={{ color: '#5b21b6', fontSize: 13, fontWeight: '500' }}>
                                            Processing...
                                        </Text>
                                    </View>
                                )}
                            </View>
                        </View>
                        {processBudgetResult && (
                            <View style={{ backgroundColor: processBudgetResult.ok ? '#ecfdf5' : '#fef2f2', border: processBudgetResult.ok ? '1px solid #a7f3d0' : '1px solid #fecaca', borderRadius: 6, padding: 12, marginBottom: 12 }}>
                                <Text style={{ fontSize: 13, fontWeight: '600', color: processBudgetResult.ok ? '#065f46' : '#7f1d1d', marginBottom: 4 }}>
                                    Process Budget Upload — {processBudgetResult.ok ? 'SUCCESS' : (processBudgetResult.error ? 'NETWORK ERROR' : 'NON-SUCCESS RESPONSE')}
                                </Text>
                                {processBudgetResult.error ? (
                                    <Text style={{ fontSize: 11, color: '#7f1d1d', fontFamily: 'monospace' }}>{processBudgetResult.error}</Text>
                                ) : processBudgetResult.results.map(r => (
                                    <Text key={r.jobId} style={{ fontSize: 11, color: r.ok ? '#065f46' : '#7f1d1d', fontFamily: 'monospace' }}>
                                        {r.jobId} — {r.ok ? 'SUCCESS' : (r.error || JSON.stringify(r.data?.message || r.data))}
                                    </Text>
                                ))}
                            </View>
                        )}
                        <Text style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
                            Active/pending Fusion jobs with no JA budget (Ledger Type JA) in JDE GL Balances.
                        </Text>

                        {budgetSyncResults && budgetSyncResults.failedDetails?.length > 0 && (
                            <View style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: 12, marginBottom: 16 }}>
                                <Text style={{ fontSize: 13, fontWeight: '600', color: '#ef4444', marginBottom: 8 }}>
                                    {budgetSyncResults.failed} upload(s) had errors:
                                </Text>
                                {budgetSyncResults.failedDetails.slice(0, 20).map((f, i) => (
                                    <Text key={i} style={{ fontSize: 12, color: '#7f1d1d' }}>
                                        {f.jobId} {f.costType ? `(${f.costType}/${f.costCode})` : ''} — {f.message}
                                    </Text>
                                ))}
                            </View>
                        )}

                        {budgetCheckLoading ? (
                            <Text style={{ color: '#6b7280', fontSize: 14 }}>Checking JDE JA budgets... ({Object.keys(jdeBudgetExists).length} / {allJobs.length})</Text>
                        ) : (() => {
                            const missingBudgetJobs = allJobs.filter(job => jdeBudgetExists[job.BU_ID] === false);
                            const checkedCount = Object.keys(jdeBudgetExists).length;
                            if (checkedCount === 0) {
                                return <Text style={{ color: '#6b7280', fontSize: 14 }}>No budget data loaded yet.</Text>;
                            }
                            if (!missingBudgetJobs.length) {
                                return <Text style={{ color: '#10b981', fontSize: 14, fontWeight: '500' }}>All {allJobs.length} jobs have a JA budget in JDE.</Text>;
                            }
                            return (
                                <View>
                                    <Text style={{ fontSize: 13, color: '#ef4444', marginBottom: 10, fontWeight: '500' }}>
                                        {missingBudgetJobs.length} of {allJobs.length} jobs missing JA budget in JDE
                                    </Text>
                                    <View style={{ borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
                                        <View style={{ flexDirection: 'row', backgroundColor: '#f1f5f9', borderBottomWidth: 1, borderColor: '#e2e8f0' }}>
                                            <Pressable onPress={() => {
                                                const missingIds = missingBudgetJobs.map(j => j.BU_ID);
                                                setSelectedBudgetJobs(prev => prev.length === missingIds.length ? [] : missingIds);
                                            }} style={{ width: 36, padding: 8, justifyContent: 'center', alignItems: 'center' }}>
                                                <Text style={{ fontSize: 14 }}>{selectedBudgetJobs.length === missingBudgetJobs.length && missingBudgetJobs.length > 0 ? '☑' : '☐'}</Text>
                                            </Pressable>
                                            <Text style={{ width: 90, padding: 8, fontSize: 12, fontWeight: '600', color: '#374151' }}>Job #</Text>
                                            <Text style={{ width: 55, padding: 8, fontSize: 12, fontWeight: '600', color: '#374151' }}>Type</Text>
                                            <Text style={{ flex: 1, padding: 8, fontSize: 12, fontWeight: '600', color: '#374151' }}>Job Name</Text>
                                            <Text style={{ width: 140, padding: 8, fontSize: 12, fontWeight: '600', color: '#374151' }}>Project Manager</Text>
                                            <Text style={{ width: 90, padding: 8, fontSize: 12, fontWeight: '600', color: '#374151' }}>Team</Text>
                                            <Text style={{ width: 110, padding: 8, fontSize: 12, fontWeight: '600', color: '#374151', textAlign: 'right' }}>Total Cost (EAC)</Text>
                                            <Text style={{ width: 110, padding: 8, fontSize: 12, fontWeight: '600', color: '#374151', textAlign: 'right' }}>Fusion Budget</Text>
                                            <Text style={{ width: 90, padding: 8, fontSize: 12, fontWeight: '600', color: '#374151', textAlign: 'center' }}>JDE JA</Text>
                                            <Text style={{ width: 110, padding: 8, fontSize: 12, fontWeight: '600', color: '#374151', textAlign: 'center' }}>JDE HA (Actuals)</Text>
                                        </View>
                                        {missingBudgetJobs.map((job, i) => (
                                            <Pressable key={job.BU_ID} onPress={() => setSelectedBudgetJobs(prev => prev.includes(job.BU_ID) ? prev.filter(id => id !== job.BU_ID) : [...prev, job.BU_ID])}>
                                                <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderColor: '#f1f5f9', backgroundColor: selectedBudgetJobs.includes(job.BU_ID) ? '#eff6ff' : i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                                                    <View style={{ width: 36, padding: 8, justifyContent: 'center', alignItems: 'center' }}>
                                                        <Text style={{ fontSize: 14 }}>{selectedBudgetJobs.includes(job.BU_ID) ? '☑' : '☐'}</Text>
                                                    </View>
                                                    <Text style={{ width: 90, padding: 8, fontSize: 12, color: '#374151' }}>{job.BU_ID}</Text>
                                                    <Text style={{ width: 55, padding: 8, fontSize: 12, color: '#6b7280' }}>{job.Cat_Code_BU_08 == 'TMN' ? 'NTE' : job.Cat_Code_BU_08}</Text>
                                                    <Text style={{ flex: 1, padding: 8, fontSize: 12, color: '#374151', textWrap: 'nowrap', overflow: 'hidden' }}>{job.BU_Description}</Text>
                                                    <Text style={{ width: 140, padding: 8, fontSize: 12, color: '#6b7280' }}>{job.Project_Manager_Name || ''}</Text>
                                                    <Text style={{ width: 90, padding: 8, fontSize: 12, color: '#6b7280' }}>{job.Cat_Code_BU_27 || ''}</Text>
                                                    <Text style={{ width: 110, padding: 8, fontSize: 12, color: '#374151', textAlign: 'right' }}>{EAC[job.BU_ID] ? fNum(pNum(EAC[job.BU_ID]), 1) : ''}</Text>
                                                    <Text style={{ width: 110, padding: 8, fontSize: 12, color: curBudget[job.BU_ID] ? '#374151' : '#ef4444', textAlign: 'right' }}>{curBudget[job.BU_ID] ? fNum(pNum(curBudget[job.BU_ID]), 1) : '—'}</Text>
                                                    <Text style={{ width: 90, padding: 8, fontSize: 12, color: '#ef4444', textAlign: 'center', fontWeight: '600' }}>Missing</Text>
                                                    <Text style={{ width: 110, padding: 8, fontSize: 12, color: jdeHAExists[job.BU_ID] === true ? '#dc2626' : jdeHAExists[job.BU_ID] === false ? '#059669' : '#9ca3af', textAlign: 'center', fontWeight: '600' }}>{jdeHAExists[job.BU_ID] === true ? 'Has Actuals ⛔' : jdeHAExists[job.BU_ID] === false ? 'None ✓' : '…'}</Text>
                                                </View>
                                            </Pressable>
                                        ))}
                                    </View>
                                </View>
                            );
                        })()}
                    </View>
                ) : null}
                {activeTab === 'Revised Budget' ? (
                    <View style={{ padding: 20 }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 12 }}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                                <Text style={{ fontSize: 16, fontWeight: '600', color: '#111827' }}>Change Orders</Text>
                                <Text style={{ fontSize: 13, color: '#6b7280' }}>
                                    {coLoading ? 'Loading...' : `${filteredCoRows.length} of ${coRows.length} CO(s) across ${new Set(coRows.map(r => r.buId)).size} job(s)`}
                                </Text>
                                <Text style={{ fontSize: 12, color: '#6b7280', marginLeft: 8 }}>Status:</Text>
                                <select
                                    value={coStatusFilter}
                                    onChange={(e) => setCoStatusFilter(e.target.value)}
                                    style={{ fontSize: 12, padding: '4px 8px', borderRadius: 4, border: '1px solid #d1d5db', backgroundColor: '#fff', color: '#111827' }}
                                >
                                    {coStatusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </View>
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                                <Pressable
                                    style={{ backgroundColor: coRows.length ? '#0ea5e9' : '#bae6fd', padding: 10, borderRadius: 5, opacity: coRows.length ? 1 : 0.5 }}
                                    onPress={downloadCoCsv}
                                    disabled={!coRows.length}
                                >
                                    <Text style={{ color: '#fff', fontWeight: '600', fontSize: 13 }}>Download CSV</Text>
                                </Pressable>
                                {!coSyncing ? (
                                    <Pressable
                                        style={{ backgroundColor: filteredCoRows.length ? '#2563eb' : '#93c5fd', padding: 10, borderRadius: 5, opacity: filteredCoRows.length ? 1 : 0.5 }}
                                        onPress={() => setShowCoSyncModal(true)}
                                        disabled={!filteredCoRows.length}
                                    >
                                        <Text style={{ color: '#fff', fontWeight: '600', fontSize: 13 }}>Sync COs to JDE</Text>
                                    </Pressable>
                                ) : (
                                    <View style={{ backgroundColor: '#dbeafe', padding: 10, borderRadius: 5 }}>
                                        <Text style={{ color: '#1e40af', fontSize: 13, fontWeight: '500' }}>
                                            Syncing... {coSyncProgress.current}/{coSyncProgress.total}
                                        </Text>
                                    </View>
                                )}
                            </View>
                        </View>

                        {coSyncResults && coSyncResults.failedDetails?.length > 0 && (
                            <View style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: 12, marginBottom: 16 }}>
                                <Text style={{ fontSize: 13, fontWeight: '600', color: '#ef4444', marginBottom: 8 }}>
                                    {coSyncResults.failed} line(s) had errors:
                                </Text>
                                {coSyncResults.failedDetails.slice(0, 20).map((f, i) => (
                                    <Text key={i} style={{ fontSize: 12, color: '#7f1d1d' }}>
                                        {f.jobId} CO#{f.coNo} {f.costType ? `(${f.costType}/${f.costCode})` : ''} — {f.message}
                                    </Text>
                                ))}
                            </View>
                        )}

                        {coLoading ? (
                            <Text style={{ color: '#6b7280', fontSize: 14 }}>Loading change order data...</Text>
                        ) : !coRows.length ? (
                            <Text style={{ color: '#6b7280', fontSize: 14 }}>No change orders found for the current selection.</Text>
                        ) : (
                            <View style={{ borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
                                <View style={{ flexDirection: 'row', backgroundColor: '#f1f5f9', borderBottomWidth: 1, borderColor: '#e2e8f0' }}>
                                    <Text style={{ width: 80, padding: 8, fontSize: 12, fontWeight: '600', color: '#374151' }}>Job #</Text>
                                    <Text style={{ width: 160, padding: 8, fontSize: 12, fontWeight: '600', color: '#374151' }}>Job Name</Text>
                                    <Text style={{ width: 60, padding: 8, fontSize: 12, fontWeight: '600', color: '#374151' }}>CO #</Text>
                                    <Text style={{ flex: 1, padding: 8, fontSize: 12, fontWeight: '600', color: '#374151' }}>Description</Text>
                                    <Text style={{ width: 110, padding: 8, fontSize: 12, fontWeight: '600', color: '#374151' }}>Status</Text>
                                    <Text style={{ width: 80, padding: 8, fontSize: 12, fontWeight: '600', color: '#374151' }}>Phase</Text>
                                    <Text style={{ width: 100, padding: 8, fontSize: 12, fontWeight: '600', color: '#374151', textAlign: 'right' }}>Revenue</Text>
                                    <Text style={{ width: 90, padding: 8, fontSize: 12, fontWeight: '600', color: '#374151', textAlign: 'right' }}>Reduction</Text>
                                    <Text style={{ width: 100, padding: 8, fontSize: 12, fontWeight: '600', color: '#374151', textAlign: 'right' }}>Cost</Text>
                                    <Text style={{ width: 100, padding: 8, fontSize: 12, fontWeight: '600', color: '#374151', textAlign: 'right' }}>GP</Text>
                                    <Text style={{ width: 55, padding: 8, fontSize: 12, fontWeight: '600', color: '#374151', textAlign: 'right' }}>GP%</Text>
                                </View>
                                {filteredCoRows.map((co, i) => {
                                    const netRev = co.revenue - co.reduction;
                                    const gp = netRev - co.cost;
                                    const gpPct = netRev ? (gp / netRev) * 100 : 0;
                                    return (
                                        <View key={co.rowId} style={{ flexDirection: 'row', borderBottomWidth: 1, borderColor: '#f1f5f9', backgroundColor: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                                            <Text style={{ width: 80, padding: 8, fontSize: 12, color: '#374151' }}>{co.buId}</Text>
                                            <Text style={{ width: 160, padding: 8, fontSize: 12, color: '#374151', textWrap: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} numberOfLines={1}>{co.jobName}</Text>
                                            <Text style={{ width: 60, padding: 8, fontSize: 12, color: '#374151' }}>{co.coNo}</Text>
                                            <Text style={{ flex: 1, padding: 8, fontSize: 12, color: '#374151', textWrap: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} numberOfLines={1}>{co.description}</Text>
                                            <Text style={{ width: 110, padding: 8, fontSize: 12, color: '#6b7280' }}>{co.status}</Text>
                                            <Text style={{ width: 80, padding: 8, fontSize: 12, color: '#6b7280' }}>{co.costCode}</Text>
                                            <Text style={{ width: 100, padding: 8, fontSize: 12, color: '#374151', textAlign: 'right' }}>{co.revenue ? fNum(co.revenue, 1) : ''}</Text>
                                            <Text style={{ width: 90, padding: 8, fontSize: 12, color: co.reduction ? '#dc2626' : '#9ca3af', textAlign: 'right' }}>{co.reduction ? fNum(co.reduction, 1) : ''}</Text>
                                            <Text style={{ width: 100, padding: 8, fontSize: 12, color: '#374151', textAlign: 'right' }}>{co.cost ? fNum(co.cost, 1) : ''}</Text>
                                            <Text style={{ width: 100, padding: 8, fontSize: 12, color: gp < 0 ? '#dc2626' : '#059669', textAlign: 'right', fontWeight: '600' }}>{netRev || co.cost ? fNum(gp, 1) : ''}</Text>
                                            <Text style={{ width: 55, padding: 8, fontSize: 12, color: gpPct < 0 ? '#dc2626' : '#374151', textAlign: 'right' }}>{netRev ? fNum(gpPct, 1) + '%' : ''}</Text>
                                        </View>
                                    );
                                })}
                            </View>
                        )}
                    </View>
                ) : null}
            </View>

            {/* Sync EAC Confirmation Modal */}
            {showSyncModal && (
                <View style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, justifyContent: 'center', alignItems: 'center' }}>
                    <View style={{ backgroundColor: '#fff', borderRadius: 10, padding: 28, width: 560, maxHeight: '80vh', overflow: 'auto', boxShadow: '0 8px 30px rgba(0,0,0,0.25)' }}>
                        <Text style={{ fontSize: 18, fontWeight: '700', color: '#1e293b', marginBottom: 20 }}>Sync EAC to JDE</Text>

                        <View style={{ backgroundColor: '#f0f9ff', borderRadius: 6, padding: 14, marginBottom: 16 }}>
                            <View style={{ flexDirection: 'row', marginBottom: 8, alignItems: 'center' }}>
                                <Text style={{ fontSize: 13, color: '#64748b', width: 140 }}>JDE Environment:</Text>
                                <View style={{ backgroundColor: envColor(jdeEnv), padding: '2px 10px', borderRadius: 4 }}>
                                    <Text style={{ fontSize: 12, color: '#fff', fontWeight: '700', textTransform: 'uppercase' }}>{jdeEnv}</Text>
                                </View>
                            </View>
                            <View style={{ flexDirection: 'row', marginBottom: 8 }}>
                                <Text style={{ fontSize: 13, color: '#64748b', width: 140 }}>Orch Endpoint:</Text>
                                <Text style={{ fontSize: 11, color: '#0c4a6e', fontWeight: '600', flex: 1, flexWrap: 'wrap' }}>{buildOrchUrl(jdeEnv, 'STR_ORCH_FusionEAC_BulkBatchUpload')}</Text>
                            </View>
                            <View style={{ flexDirection: 'row', marginBottom: 8 }}>
                                <Text style={{ fontSize: 13, color: '#64748b', width: 140 }}>Version Date:</Text>
                                <Text style={{ fontSize: 13, color: isLiveVersion ? '#0c4a6e' : '#b91c1c', fontWeight: '600' }}>{versionInfo?.Version_Start_DateTime ? moment(versionInfo.Version_Start_DateTime).format('MMM D, YYYY') : '—'}</Text>
                            </View>
                            {!isLiveVersion && (
                                <Text style={{ fontSize: 12, color: '#b91c1c', marginBottom: 8, marginLeft: 140 }}>
                                    Not the latest version. The file will carry this version's numbers. Switch to the latest version unless that's intended.
                                </Text>
                            )}
                            <View style={{ flexDirection: 'row', marginBottom: 8 }}>
                                <Text style={{ fontSize: 13, color: '#64748b', width: 140 }}>Sync To Period:</Text>
                                <Text style={{ fontSize: 13, color: '#0c4a6e', fontWeight: '600' }}>
                                    {currentPeriod.displayDate} (P{currentPeriod.periodNbr} FY{currentPeriod.fiscalYearShort})
                                </Text>
                            </View>
                            <View style={{ flexDirection: 'row', marginBottom: 8 }}>
                                <Text style={{ fontSize: 13, color: '#64748b', width: 140 }}>File Lines:</Text>
                                <Text style={{ fontSize: 13, color: '#0c4a6e', fontWeight: '600' }}>
                                    {eacRows ? eacRows.length : eacFileLineCount}{eacZeroLines?.length ? ` + ${eacZeroLines.length} zero lines` : ''}
                                </Text>
                            </View>
                            <View style={{ flexDirection: 'row' }}>
                                <Text style={{ fontSize: 13, color: '#64748b', width: 140 }}>Jobs:</Text>
                                <Text style={{ fontSize: 13, color: '#0c4a6e', fontWeight: '600' }}>
                                    {exportJobs.length}
                                    {tickedCount ? ' selected' : ''}
                                    {teamFilter !== 'All' ? ` (${TEAM_LABELS[teamFilter] || teamFilter})` : ''}
                                    {tickedCount && tickedCount <= 12 ? `: ${exportJobs.map(j => j.BU_ID).join(', ')}` : ''}
                                </Text>
                            </View>
                        </View>

                        {/* Old HA accounts the file doesn't carry get a 0 line so they don't double-count */}
                        <View style={{ backgroundColor: '#f8fafc', borderRadius: 6, padding: 14, marginBottom: 16 }}>
                            <View style={{ flexDirection: 'row' }}>
                                <Text style={{ fontSize: 13, color: '#64748b', width: 140 }}>Old HA accounts:</Text>
                                {eacZeroLines === null ? (
                                    <Text style={{ fontSize: 13, color: '#64748b' }}>Checking JDE HA...</Text>
                                ) : eacZeroInfo.failed ? (
                                    <Text style={{ fontSize: 13, color: '#b91c1c', flex: 1 }}>
                                        Couldn't check ({eacZeroInfo.failed}). Syncing now would leave old HA balances in place and double-count, so it's blocked.
                                    </Text>
                                ) : eacZeroLines.length ? (
                                    <Text style={{ fontSize: 13, color: '#0c4a6e', fontWeight: '600', flex: 1 }}>
                                        {eacZeroLines.length} to clear ({fNum(eacZeroLines.reduce((s, z) => s + z.clears, 0), 1)}) on {new Set(eacZeroLines.map(z => z.jobId)).size} jobs
                                    </Text>
                                ) : (
                                    <Text style={{ fontSize: 13, color: '#0c4a6e', fontWeight: '600' }}>None, every HA account is in the file</Text>
                                )}
                            </View>
                            {eacZeroLines !== null && !eacZeroInfo.failed && (
                                <View style={{ flexDirection: 'row', marginTop: 8 }}>
                                    <Text style={{ fontSize: 13, color: '#64748b', width: 140 }}>No-phase lines:</Text>
                                    <Text style={{ fontSize: 13, color: eacZeroInfo.accountsChecked ? '#0c4a6e' : '#b45309', flex: 1 }}>
                                        {!eacZeroInfo.accountsChecked
                                            ? 'Couldn\'t check JDE accounts, so they go on 100105 as before.'
                                            : eacZeroInfo.moved
                                                ? `${eacZeroInfo.moved} moved to a no-phase account the job has in JDE (100101, 100105 or 100102)`
                                                : 'All on accounts JDE has'}
                                    </Text>
                                </View>
                            )}
                            {eacZeroInfo.source === 'warehouse' && (
                                <Text style={{ fontSize: 11, color: '#92400e', marginTop: 6 }}>
                                    Read from the warehouse copy, which can be ~2 hours behind JDE.
                                </Text>
                            )}
                        </View>

                        {/* Where the file goes */}
                        <View style={{ backgroundColor: '#f8fafc', borderRadius: 6, padding: 14, marginBottom: 16 }}>
                            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                <Text style={{ fontSize: 13, color: '#64748b', width: 140 }}>Input Folder:</Text>
                                <Text style={{ fontSize: 13, color: eacFolder ? '#0c4a6e' : '#b45309', fontWeight: '600', flex: 1 }}>
                                    {eacFolder ? `${eacFolder.name} (chosen)` : 'Not chosen yet'}
                                </Text>
                                {folderWriteSupported() && (
                                    <Pressable
                                        style={{ backgroundColor: '#e2e8f0', padding: '6px 12px', borderRadius: 5 }}
                                        onPress={chooseEacFolder}
                                    >
                                        <Text style={{ fontSize: 12, fontWeight: '600', color: '#334155' }}>{eacFolder ? 'Change' : 'Choose folder'}</Text>
                                    </Pressable>
                                )}
                            </View>
                            <Text style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>
                                {inputFolderPath(jdeEnv)} — {EAC_FILE_NAME} is written here, then the orchestrator picks it up.
                            </Text>
                            {!folderWriteSupported() && (
                                <Text style={{ fontSize: 12, color: '#b91c1c', marginTop: 6 }}>
                                    This browser can't write to folders. Use Edge or Chrome, or download the file and drop it in the folder.
                                </Text>
                            )}
                            {eacFolderError ? (
                                <Text style={{ fontSize: 12, color: '#b91c1c', marginTop: 6 }}>{eacFolderError}</Text>
                            ) : null}
                            {!eacFolder && (
                                <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 10 }}>
                                    <Text style={{ fontSize: 12, color: '#475569', flex: 1 }}>
                                        Put {EAC_FILE_NAME} in the folder yourself? Run the sync on it without choosing the folder.
                                    </Text>
                                    <Pressable
                                        style={{ backgroundColor: '#2563eb', padding: '6px 12px', borderRadius: 5, opacity: eacWriting ? 0.5 : 1 }}
                                        onPress={syncPlacedEacFile}
                                        disabled={eacWriting}
                                    >
                                        <Text style={{ fontSize: 12, fontWeight: '600', color: '#fff' }}>Sync file in folder</Text>
                                    </Pressable>
                                </View>
                            )}
                        </View>

                        <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 12, marginTop: 8 }}>
                            <Pressable
                                style={{ backgroundColor: '#e2e8f0', padding: 12, paddingLeft: 28, paddingRight: 28, borderRadius: 6 }}
                                onPress={() => setShowSyncModal(false)}
                            >
                                <Text style={{ fontSize: 14, fontWeight: '600', color: '#475569' }}>Cancel</Text>
                            </Pressable>
                            <Pressable
                                style={{ backgroundColor: '#e0f2fe', padding: 12, paddingLeft: 20, paddingRight: 20, borderRadius: 6, opacity: eacZeroLines === null ? 0.5 : 1 }}
                                onPress={downloadJdeEacFile}
                                disabled={eacZeroLines === null}
                            >
                                <Text style={{ fontSize: 14, fontWeight: '600', color: '#0369a1' }}>Download File</Text>
                            </Pressable>
                            <Pressable
                                style={{ backgroundColor: '#2563eb', padding: 12, paddingLeft: 28, paddingRight: 28, borderRadius: 6, opacity: (!eacFolder || eacZeroLines === null || eacZeroInfo.failed || eacWriting) ? 0.5 : 1 }}
                                onPress={syncEACToJDE}
                                disabled={!eacFolder || eacZeroLines === null || !!eacZeroInfo.failed || eacWriting}
                            >
                                <Text style={{ fontSize: 14, fontWeight: '600', color: '#fff' }}>{eacWriting ? 'Writing file...' : 'Write File & Sync'}</Text>
                            </Pressable>
                        </View>
                    </View>
                </View>
            )}

            {/* Budget Sync Confirmation Modal */}
            {showBudgetSyncModal && (
                <View style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, justifyContent: 'center', alignItems: 'center' }}>
                    <View style={{ backgroundColor: '#fff', borderRadius: 10, padding: 28, width: 720, maxHeight: '85vh', overflow: 'auto', boxShadow: '0 8px 30px rgba(0,0,0,0.25)' }}>
                        <Text style={{ fontSize: 18, fontWeight: '700', color: '#1e293b', marginBottom: 20 }}>Sync Budgets to JDE</Text>

                        <View style={{ backgroundColor: '#f0f9ff', borderRadius: 6, padding: 14, marginBottom: 16 }}>
                            <View style={{ flexDirection: 'row', marginBottom: 8, alignItems: 'center' }}>
                                <Text style={{ fontSize: 13, color: '#64748b', width: 160 }}>JDE Environment:</Text>
                                <View style={{ backgroundColor: envColor(jdeEnv), padding: '2px 10px', borderRadius: 4 }}>
                                    <Text style={{ fontSize: 12, color: '#fff', fontWeight: '700', textTransform: 'uppercase' }}>{jdeEnv}</Text>
                                </View>
                            </View>
                            <View style={{ flexDirection: 'row', marginBottom: 8 }}>
                                <Text style={{ fontSize: 13, color: '#64748b', width: 160 }}>Upload Orch:</Text>
                                <Text style={{ fontSize: 11, color: '#0c4a6e', fontWeight: '600', flex: 1, flexWrap: 'wrap' }}>{buildOrchUrl(jdeEnv, 'STR_ORCH_Fusion_UploadBudget')}</Text>
                            </View>
                            <View style={{ flexDirection: 'row', marginBottom: 8 }}>
                                <Text style={{ fontSize: 13, color: '#64748b', width: 160 }}>Process Orch:</Text>
                                <Text style={{ fontSize: 11, color: '#0c4a6e', fontWeight: '600', flex: 1, flexWrap: 'wrap' }}>{buildOrchUrl(jdeEnv, 'STR_ORCH_Fusion_ProcessBudgetUpload')}</Text>
                            </View>
                            <View style={{ flexDirection: 'row', marginBottom: 8 }}>
                                <Text style={{ fontSize: 13, color: '#64748b', width: 160 }}>Period:</Text>
                                <Text style={{ fontSize: 13, color: '#0c4a6e', fontWeight: '600' }}>
                                    {currentPeriod.displayDate} (P{currentPeriod.periodNbr} FY{currentPeriod.fiscalYearShort}, Cent {currentPeriod.century})
                                </Text>
                            </View>
                            <View style={{ flexDirection: 'row', marginBottom: 8 }}>
                                <Text style={{ fontSize: 13, color: '#64748b', width: 160 }}>Period Params:</Text>
                                <Text style={{ fontSize: 11, color: '#0c4a6e', fontWeight: '600' }}>
                                    {`{ Cent: ${currentPeriod.century}, FY: ${currentPeriod.fiscalYearShort}, BegPeriod: ${currentPeriod.periodNbr}, EndPeriod: ${currentPeriod.periodNbr}, BegFY: ${currentPeriod.fiscalYear}, EndFY: ${currentPeriod.fiscalYear} }`}
                                </Text>
                            </View>
                            <View style={{ flexDirection: 'row' }}>
                                <Text style={{ fontSize: 13, color: '#64748b', width: 160 }}>Jobs to Sync:</Text>
                                <Text style={{ fontSize: 13, color: '#0c4a6e', fontWeight: '700' }}>{budgetJobsToSync.length}</Text>
                            </View>
                        </View>

                        {budgetJobsBlockedByHA.length > 0 && (
                            <View style={{ backgroundColor: '#fef9c3', border: '1px solid #fde047', borderRadius: 6, padding: 12, marginBottom: 16 }}>
                                <Text style={{ fontSize: 13, fontWeight: '700', color: '#854d0e', marginBottom: 6 }}>
                                    Skipping {budgetJobsBlockedByHA.length} job(s) — HA (actuals) already in JDE
                                </Text>
                                <Text style={{ fontSize: 11, color: '#854d0e', marginBottom: 8, fontStyle: 'italic' }}>
                                    Budget upload is blocked for jobs that already have actuals posted. These will not be sent.
                                </Text>
                                <View style={{ maxHeight: 120, overflow: 'auto' }}>
                                    {budgetJobsBlockedByHA.map(j => (
                                        <Text key={j.BU_ID} style={{ fontSize: 12, color: '#713f12', paddingVertical: 1 }}>
                                            • {j.BU_ID} — {j.BU_Description}
                                        </Text>
                                    ))}
                                </View>
                            </View>
                        )}
                        <Text style={{ fontSize: 13, fontWeight: '600', color: '#374151', marginBottom: 8 }}>
                            Jobs with Fusion budget but missing JA budget in JDE:
                        </Text>
                        <View style={{ borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 6, overflow: 'hidden', marginBottom: 16, maxHeight: 300 }}>
                            <View style={{ flexDirection: 'row', backgroundColor: '#f1f5f9', borderBottomWidth: 1, borderColor: '#e2e8f0' }}>
                                <Text style={{ width: 90, padding: 8, fontSize: 12, fontWeight: '600', color: '#374151' }}>Job #</Text>
                                <Text style={{ flex: 1, padding: 8, fontSize: 12, fontWeight: '600', color: '#374151' }}>Job Name</Text>
                                <Text style={{ width: 110, padding: 8, fontSize: 12, fontWeight: '600', color: '#374151', textAlign: 'right' }}>Fusion Budget</Text>
                                <Text style={{ width: 80, padding: 8, fontSize: 12, fontWeight: '600', color: '#374151', textAlign: 'center' }}>JDE JA</Text>
                                <Text style={{ width: 110, padding: 8, fontSize: 12, fontWeight: '600', color: '#374151', textAlign: 'center' }}>JDE HA</Text>
                            </View>
                            <View style={{ overflow: 'auto', maxHeight: 260 }}>
                                {budgetJobsToSync.map((job, i) => (
                                    <View key={job.BU_ID} style={{ flexDirection: 'row', borderBottomWidth: 1, borderColor: '#f1f5f9', backgroundColor: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                                        <Text style={{ width: 90, padding: 8, fontSize: 12, color: '#374151' }}>{job.BU_ID}</Text>
                                        <Text style={{ flex: 1, padding: 8, fontSize: 12, color: '#374151', textWrap: 'nowrap', overflow: 'hidden' }}>{job.BU_Description}</Text>
                                        <Text style={{ width: 110, padding: 8, fontSize: 12, color: curBudget[job.BU_ID] ? '#059669' : '#ef4444', textAlign: 'right', fontWeight: '600' }}>{curBudget[job.BU_ID] ? fNum(pNum(curBudget[job.BU_ID]), 1) : '—'}</Text>
                                        <Text style={{ width: 80, padding: 8, fontSize: 12, color: '#ef4444', textAlign: 'center', fontWeight: '600' }}>Missing</Text>
                                        <Text style={{ width: 110, padding: 8, fontSize: 12, color: jdeHAExists[job.BU_ID] === true ? '#dc2626' : '#059669', textAlign: 'center', fontWeight: '600' }}>{jdeHAExists[job.BU_ID] === true ? 'Has Actuals' : 'None ✓'}</Text>
                                    </View>
                                ))}
                            </View>
                        </View>

                        <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 12, marginTop: 8 }}>
                            <Pressable
                                style={{ backgroundColor: '#e2e8f0', padding: 12, paddingLeft: 28, paddingRight: 28, borderRadius: 6 }}
                                onPress={() => setShowBudgetSyncModal(false)}
                            >
                                <Text style={{ fontSize: 14, fontWeight: '600', color: '#475569' }}>Cancel</Text>
                            </Pressable>
                            <Pressable
                                style={{ backgroundColor: '#2563eb', padding: 12, paddingLeft: 28, paddingRight: 28, borderRadius: 6 }}
                                onPress={syncBudgetsToJDE}
                            >
                                <Text style={{ fontSize: 14, fontWeight: '600', color: '#fff' }}>Confirm Sync to JDE</Text>
                            </Pressable>
                        </View>
                    </View>
                </View>
            )}

            {/* Process Budget Upload Confirmation Modal */}
            {showProcessBudgetModal && (
                <View style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, justifyContent: 'center', alignItems: 'center' }}>
                    <View style={{ backgroundColor: '#fff', borderRadius: 10, padding: 28, width: 640, maxHeight: '85vh', overflow: 'auto', boxShadow: '0 8px 30px rgba(0,0,0,0.25)' }}>
                        <Text style={{ fontSize: 18, fontWeight: '700', color: '#1e293b', marginBottom: 8 }}>Run Process Budget Upload</Text>
                        <Text style={{ fontSize: 12, color: '#6b7280', marginBottom: 20 }}>
                            Posts the staged budget rows for each ticked job. Sync to JDE already posts
                            every job it syncs; use this for a job whose rows were staged but not posted.
                        </Text>

                        <View style={{ backgroundColor: '#f5f3ff', borderRadius: 6, padding: 14, marginBottom: 16 }}>
                            <View style={{ flexDirection: 'row', marginBottom: 8, alignItems: 'center' }}>
                                <Text style={{ fontSize: 13, color: '#64748b', width: 160 }}>JDE Environment:</Text>
                                <View style={{ backgroundColor: envColor(jdeEnv), padding: '2px 10px', borderRadius: 4 }}>
                                    <Text style={{ fontSize: 12, color: '#fff', fontWeight: '700', textTransform: 'uppercase' }}>{jdeEnv}</Text>
                                </View>
                            </View>
                            <View style={{ flexDirection: 'row', marginBottom: 8 }}>
                                <Text style={{ fontSize: 13, color: '#64748b', width: 160 }}>Process Orch:</Text>
                                <Text style={{ fontSize: 11, color: '#4c1d95', fontWeight: '600', flex: 1, flexWrap: 'wrap' }}>{buildOrchUrl(jdeEnv, 'STR_ORCH_Fusion_ProcessBudgetUpload')}</Text>
                            </View>
                            <View style={{ flexDirection: 'row', marginBottom: 8 }}>
                                <Text style={{ fontSize: 13, color: '#64748b', width: 160 }}>Period:</Text>
                                <Text style={{ fontSize: 13, color: '#4c1d95', fontWeight: '600' }}>
                                    {currentPeriod.displayDate} (P{currentPeriod.periodNbr} FY{currentPeriod.fiscalYearShort}, Cent {currentPeriod.century})
                                </Text>
                            </View>
                            <View style={{ flexDirection: 'row', marginBottom: 8 }}>
                                <Text style={{ fontSize: 13, color: '#64748b', width: 160 }}>Payload (per job):</Text>
                                <Text style={{ fontSize: 11, color: '#4c1d95', fontFamily: 'monospace', flex: 1 }}>
                                    {`{ Cent: ${currentPeriod.century}, FY: ${currentPeriod.fiscalYearShort}, BegPeriod: 01, EndPeriod: ${currentPeriod.periodNbr}, BegFY: ${currentPeriod.fiscalYear}, EndFY: ${currentPeriod.fiscalYear}, BusUnit }`}
                                </Text>
                            </View>
                            <View style={{ flexDirection: 'row' }}>
                                <Text style={{ fontSize: 13, color: '#64748b', width: 160 }}>Ticked jobs:</Text>
                                <Text style={{ fontSize: 13, color: '#4c1d95', fontWeight: '600', flex: 1, flexWrap: 'wrap' }}>
                                    {selectedBudgetJobs.length ? selectedBudgetJobs.join(', ') : 'None. Tick the jobs to post in the list first.'}
                                </Text>
                            </View>
                        </View>

                        <View style={{ backgroundColor: '#fefce8', border: '1px solid #fde68a', borderRadius: 6, padding: 12, marginBottom: 16 }}>
                            <Text style={{ fontSize: 12, color: '#854d0e', lineHeight: 18 }}>
                                Posts to GL Balances and recalculates projections for each job. JDE posts every row
                                staged for the job, replacing each account's budget, and emails the reports.
                                Verify environment and period above before continuing.
                            </Text>
                        </View>

                        <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 12, marginTop: 8 }}>
                            <Pressable
                                style={{ backgroundColor: '#e2e8f0', padding: 12, paddingLeft: 28, paddingRight: 28, borderRadius: 6 }}
                                onPress={() => setShowProcessBudgetModal(false)}
                            >
                                <Text style={{ fontSize: 14, fontWeight: '600', color: '#475569' }}>Cancel</Text>
                            </Pressable>
                            <Pressable
                                style={{ backgroundColor: '#7c3aed', padding: 12, paddingLeft: 28, paddingRight: 28, borderRadius: 6, opacity: selectedBudgetJobs.length ? 1 : 0.5 }}
                                onPress={runProcessBudgetUpload}
                                disabled={!selectedBudgetJobs.length}
                            >
                                <Text style={{ fontSize: 14, fontWeight: '600', color: '#fff' }}>Confirm — Run Process</Text>
                            </Pressable>
                        </View>
                    </View>
                </View>
            )}

            {/* CO Sync Confirmation Modal */}
            {showCoSyncModal && (
                <View style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, justifyContent: 'center', alignItems: 'center' }}>
                    <View style={{ backgroundColor: '#fff', borderRadius: 10, padding: 28, width: 720, maxHeight: '85vh', overflow: 'auto', boxShadow: '0 8px 30px rgba(0,0,0,0.25)' }}>
                        <Text style={{ fontSize: 18, fontWeight: '700', color: '#1e293b', marginBottom: 20 }}>Sync Change Orders to JDE</Text>

                        <View style={{ backgroundColor: '#f0f9ff', borderRadius: 6, padding: 14, marginBottom: 16 }}>
                            <View style={{ flexDirection: 'row', marginBottom: 8, alignItems: 'center' }}>
                                <Text style={{ fontSize: 13, color: '#64748b', width: 160 }}>JDE Environment:</Text>
                                <View style={{ backgroundColor: envColor(jdeEnv), padding: '2px 10px', borderRadius: 4 }}>
                                    <Text style={{ fontSize: 12, color: '#fff', fontWeight: '700', textTransform: 'uppercase' }}>{jdeEnv}</Text>
                                </View>
                            </View>
                            <View style={{ flexDirection: 'row', marginBottom: 8 }}>
                                <Text style={{ fontSize: 13, color: '#64748b', width: 160 }}>Upload Orch:</Text>
                                <Text style={{ fontSize: 11, color: '#0c4a6e', fontWeight: '600', flex: 1, flexWrap: 'wrap' }}>{buildOrchUrl(jdeEnv, 'STR_ORCH_Fusion_UploadBudget')}</Text>
                            </View>
                            <View style={{ flexDirection: 'row', marginBottom: 8 }}>
                                <Text style={{ fontSize: 13, color: '#64748b', width: 160 }}>Process Orch:</Text>
                                <Text style={{ fontSize: 11, color: '#0c4a6e', fontWeight: '600', flex: 1, flexWrap: 'wrap' }}>{buildOrchUrl(jdeEnv, 'STR_ORCH_Fusion_ProcessBudgetUpload')}</Text>
                            </View>
                            <View style={{ flexDirection: 'row', marginBottom: 8 }}>
                                <Text style={{ fontSize: 13, color: '#64748b', width: 160 }}>Period:</Text>
                                <Text style={{ fontSize: 13, color: '#0c4a6e', fontWeight: '600' }}>
                                    {currentPeriod.displayDate} (P{currentPeriod.periodNbr} FY{currentPeriod.fiscalYearShort}, Cent {currentPeriod.century})
                                </Text>
                            </View>
                            <View style={{ flexDirection: 'row', marginBottom: 8 }}>
                                <Text style={{ fontSize: 13, color: '#64748b', width: 160 }}>Period Params:</Text>
                                <Text style={{ fontSize: 11, color: '#0c4a6e', fontWeight: '600' }}>
                                    {`{ Cent: ${currentPeriod.century}, FY: ${currentPeriod.fiscalYearShort}, BegPeriod: ${currentPeriod.periodNbr}, EndPeriod: ${currentPeriod.periodNbr}, BegFY: ${currentPeriod.fiscalYear}, EndFY: ${currentPeriod.fiscalYear} }`}
                                </Text>
                            </View>
                            <View style={{ flexDirection: 'row' }}>
                                <Text style={{ fontSize: 13, color: '#64748b', width: 160 }}>COs to Sync:</Text>
                                <Text style={{ fontSize: 13, color: '#0c4a6e', fontWeight: '700' }}>{filteredCoRows.length} (status: {coStatusFilter})</Text>
                            </View>
                        </View>

                        <View style={{ backgroundColor: '#fefce8', border: '1px solid #fde68a', borderRadius: 6, padding: 12, marginBottom: 16 }}>
                            <Text style={{ fontSize: 12, color: '#854d0e', lineHeight: 18 }}>
                                Each CO is uploaded as one revenue line (cost type 41000, net revenue negated) plus one line per cost type (5xxxx). Sub = Phase/Cost Code on the CO.
                            </Text>
                        </View>

                        <Text style={{ fontSize: 13, fontWeight: '600', color: '#374151', marginBottom: 8 }}>
                            Preview ({Math.min(filteredCoRows.length, 20)} of {filteredCoRows.length}):
                        </Text>
                        <View style={{ borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 6, overflow: 'hidden', marginBottom: 16, maxHeight: 300 }}>
                            <View style={{ flexDirection: 'row', backgroundColor: '#f1f5f9', borderBottomWidth: 1, borderColor: '#e2e8f0' }}>
                                <Text style={{ width: 80, padding: 8, fontSize: 12, fontWeight: '600', color: '#374151' }}>Job #</Text>
                                <Text style={{ width: 60, padding: 8, fontSize: 12, fontWeight: '600', color: '#374151' }}>CO #</Text>
                                <Text style={{ flex: 1, padding: 8, fontSize: 12, fontWeight: '600', color: '#374151' }}>Description</Text>
                                <Text style={{ width: 100, padding: 8, fontSize: 12, fontWeight: '600', color: '#374151', textAlign: 'right' }}>Net Revenue</Text>
                                <Text style={{ width: 100, padding: 8, fontSize: 12, fontWeight: '600', color: '#374151', textAlign: 'right' }}>Cost</Text>
                            </View>
                            <View style={{ overflow: 'auto', maxHeight: 260 }}>
                                {filteredCoRows.slice(0, 20).map((co, i) => {
                                    const netRev = co.revenue - co.reduction;
                                    return (
                                        <View key={co.rowId} style={{ flexDirection: 'row', borderBottomWidth: 1, borderColor: '#f1f5f9', backgroundColor: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                                            <Text style={{ width: 80, padding: 8, fontSize: 12, color: '#374151' }}>{co.buId}</Text>
                                            <Text style={{ width: 60, padding: 8, fontSize: 12, color: '#374151' }}>{co.coNo}</Text>
                                            <Text style={{ flex: 1, padding: 8, fontSize: 12, color: '#374151', textWrap: 'nowrap', overflow: 'hidden' }} numberOfLines={1}>{co.description}</Text>
                                            <Text style={{ width: 100, padding: 8, fontSize: 12, color: '#374151', textAlign: 'right' }}>{netRev ? fNum(netRev, 1) : ''}</Text>
                                            <Text style={{ width: 100, padding: 8, fontSize: 12, color: '#374151', textAlign: 'right' }}>{co.cost ? fNum(co.cost, 1) : ''}</Text>
                                        </View>
                                    );
                                })}
                            </View>
                        </View>

                        <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 12, marginTop: 8 }}>
                            <Pressable
                                style={{ backgroundColor: '#e2e8f0', padding: 12, paddingLeft: 28, paddingRight: 28, borderRadius: 6 }}
                                onPress={() => setShowCoSyncModal(false)}
                            >
                                <Text style={{ fontSize: 14, fontWeight: '600', color: '#475569' }}>Cancel</Text>
                            </Pressable>
                            <Pressable
                                style={{ backgroundColor: '#2563eb', padding: 12, paddingLeft: 28, paddingRight: 28, borderRadius: 6 }}
                                onPress={syncCoToJDE}
                            >
                                <Text style={{ fontSize: 14, fontWeight: '600', color: '#fff' }}>Confirm Sync to JDE</Text>
                            </Pressable>
                        </View>
                    </View>
                </View>
            )}
        </View>
    );
};

export default ProjectionSync;
